import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { QuoteTotalStrip } from '@/components/QuoteTotalStrip';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Plus, Trash2, Save, Edit2, AlertTriangle, AlertCircle, TrendingUp, DollarSign, Lock, Copy, RefreshCw, X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import UndoToolbar from '@/components/UndoToolbar';
import FlashingTemplatePicker from '@/components/FlashingTemplatePicker';
import FlashingProfileSVG from '@/components/FlashingProfileSVG';
import SpoutingGutterDetailsBar from '@/components/SpoutingGutterDetailsBar';
import RateDetailEditButton, { RateDetailProvider } from '@/components/RateDetailEditButton';
// Iter220 — Shared library: row UI + helpers extracted so the Repairs
// builders (Iron / Concrete / Tin-Tile) can adopt the same row UX.
// The local helpers + constants below are deprecated copies kept inline
// for in-file usage; they delegate to the shared exports for behaviour
// parity. Future cleanup pass will switch the in-file usages over and
// drop the duplicates.
import ComponentLineRow from '@/components/shared/ComponentRow/ComponentLineRow';
import MaterialsOrderListModal from '@/components/MaterialsOrderListModal';
import ValidationErrorBanner from '@/components/ValidationErrorBanner';
import BuildConfirmationStrip from '@/components/BuildConfirmationStrip';
import { QuotationTypeSelect } from '@/components/QuotationTypeSelect';  // Go-Live Batch S · manual build lock
import SaveDocButton from '@/components/SaveDocButton';
import { useCascadeEngine, evaluateCascade } from '@/hooks/useCascadeEngine';

// Phase C · C4a shell extraction (2026-02c) — constants + pure helpers
// live in sibling modules so per-work-type subforms (C4b/c/d) can
// import them without pulling the mega-file.
import {
  RATE_MODAL_PRESET_BY_SECTION,
  WORK_TYPES,
  PREFIX_BY_TYPE,
  UNIT_BASED_TYPES,
  MITRE_TAG,
  AUTO_BRACKET_TAG,
} from './AppraisalComponents/constants';
import {
  fmt,
  buildSheetProfileMap,
  isMitreLine,
  isAutoBracketLine,
  stripTag,
  withMitreTag,
  lineTotals,
  inferTypeName,
  isGutterLengthLine,
  spoutingRowsFor,
  spoutRatePatch,
} from './AppraisalComponents/helpers';
// Phase C · C4b/c/d — per-work-type modules. Cascades live in hooks · UI
// adornments live in section-adornment files · state stays in the parent.
import { useFasteningCascade } from './AppraisalComponents/hooks/useFasteningCascade';
import { useTileRescrewRowCascade } from './AppraisalComponents/hooks/useTileRescrewRowCascade';
import { useSheetProfileCascade } from './AppraisalComponents/hooks/useSheetProfileCascade';
import { useUnderlayCascade } from './AppraisalComponents/hooks/useUnderlayCascade';
// Iter316 · P0-1 — Flashing rate cascade (Back Tray / Apron / Head /
// Gutter Flashing / etc. auto-pick their catalogue rate row from the
// pin's girth_mm + folds_qty + material). Mirrors useUnderlayCascade
// with the same auto-fill-then-self-heal semantics.
import { useFlashingRateCascade } from './AppraisalComponents/hooks/useFlashingRateCascade';
// C4g (2026-02c) — Row-scoped repairs material carry-through cascade.
import { useRepairsRowCascade } from './AppraisalComponents/hooks/useRepairsRowCascade';
import {
  FasteningTotalScrewsBadge,
  FasteningCascadeBar,
} from './AppraisalComponents/FasteningSectionAdornments';
import {
  SheetSectionProfilePicker,
  SheetSectionTotals,
} from './AppraisalComponents/SheetSectionAdornments';
// C4d — Aquaseal + Flashing extraction (2026-02c) · Bay-tray link cascade
// as a pure helper (sets up the future `row_link` engine primitive) +
// standalone section bars.
import {
  _aquasealLineMatch,
  cascadeDeleteFlashingToAquaseal,
  cascadeDeleteAquasealToFlashing,
} from './AppraisalComponents/helpers/baytrayLinkCascade';
import { AquasealExtraClipsBar } from './AppraisalComponents/AquasealExtraClipsBar';
import { FlashingEdgingBar } from './AppraisalComponents/FlashingEdgingBar';
// C4e — Ridge / Valley / Barge / Spouting standalone bars (2026-02c).
import { SpoutingBracketSummary } from './AppraisalComponents/SpoutingBracketSummary';
import { MultidekClipsBar } from './AppraisalComponents/MultidekClipsBar';
import { BedPointMaterialsBar } from './AppraisalComponents/BedPointMaterialsBar';
import { FoamFillerBar } from './AppraisalComponents/FoamFillerBar';
// C4f — Section accordion (loop body extracted 2026-02c).
import { SectionAccordion } from './AppraisalComponents/SectionAccordion';
// C4h — Engine status badge (2026-02c).
import { EngineStatusBadge } from './AppraisalComponents/EngineStatusBadge';
import { canonicalMaterial, primaryMaterialsForRoof } from '@/lib/materials';

// ────────────────────────────────────────────────────────────────────────
// Material narrow-filter — canonical registry lives in
// `./shared/ComponentRow/helpers.js` (single source of truth). Both the
// Components tab AND the shared ComponentLineRow read from there.
// ────────────────────────────────────────────────────────────────────────
import {
  MATERIAL_SECTIONS,
  MATERIAL_DISPLAY_LABEL,
  PRIMARY_TO_SECTION_OVERRIDE,
  PRIMARY_TO_GUTTER_MATERIAL,
  detectMaterialFor,
  rateHasMaterial,
  buildMaterialBuckets,
} from '@/components/shared/ComponentRow/helpers';

// ────────────────────────────────────────────────────────────────────────
// Cascade inference (Phase C · C3c-β · 2026-02) — material inference is
// now owned exclusively by the backend rules engine (`useCascadeEngine`
// hook · POST /api/rules-engine/evaluate · seed rules QTM-* / IMF-* /
// RTP-* / FST-*). The former local `inferMaterialFromContext` + its
// `QUOTE_TYPE_TO_MATERIAL` regex table used to double as a safety-net
// fallback until C3c-α wired the engine in. Deleted 2026-02c per
// go-live-wipe directive · engine is single source of truth.
// ────────────────────────────────────────────────────────────────────────

// Builder-pushed lines are owned by their builder — client-side material auto-fill leaves them alone.
const isBuilderLine = (l) => /_builder$/.test(l.import_source || '');

export default function AppraisalComponentsTab({ api, appraisalId, isAdmin, refreshKey = 0, onJumpTab, onlySections = null }) {
  const [groups, setGroups] = useState({});
  // Builder lines whose type has no section on this tab (e.g. Extra Painting,
  // Stripping) — read-only here but counted in the total like the PDF does.
  const [otherLines, setOtherLines] = useState([]);
  const [rates, setRates] = useState({});       // { Cleaning: [...rate rows] }
  const [openTypes, setOpenTypes] = useState(new Set());
  // v10.29 Sprint 4a · Slice C (2026-02c) — read `focus_section` from
  // URL so the Reroof Builder's fast-jump ("↗ Open in Components")
  // buttons can pinpoint a section. On mount / URL-change: expand the
  // matching WT accordion, scroll it into view, flash a 2s highlight,
  // then clear the param so a page refresh doesn't re-fire the jump.
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [undoRefresh, setUndoRefresh] = useState(0);
  // Iter190 — Autosave: persist edits to the backend automatically a
  // few seconds after the operator stops typing. Preference persists
  // in localStorage so each user keeps their preferred mode across
  // sessions. Undo still works because the existing UndoToolbar
  // snapshots the previous component_lines on every PUT.
  // Embedded mode only saves after the operator actually touches the embedded rows.
  const [embedTouched, setEmbedTouched] = useState(false);
  const [autosaveEnabled, setAutosaveEnabled] = useState(() => {
    try { return localStorage.getItem('compTab.autosave') !== '0'; } // default ON
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('compTab.autosave', autosaveEnabled ? '1' : '0'); }
    catch { /* ignore */ }
  }, [autosaveEnabled]);
  // Used to surface "Autosaving in 2s…" to the operator while the
  // debounce window is pending. -1 = no pending autosave.
  const [autosaveCountdown, setAutosaveCountdown] = useState(-1);
  const autosaveTimerRef = useRef(null);
  const autosaveCountdownTimerRef = useRef(null);
  const saveRef = useRef(null);  // late-bind so the debounce closure picks up the latest save()
  // Multi-select bulk-delete: Set of line `_key`s currently ticked.
  // Toolbar with "Delete N selected" appears whenever this is non-empty.
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  // "Clear & rebuild" dropdown — Clear all / Push from Roof Diagram /
  // Push from a Re-roof spec. Anchored to the top toolbar.
  const [rebuildMenuOpen, setRebuildMenuOpen] = useState(false);
  // Re-roof spec list for the "Push from spec" sub-menu.
  const [reroofSpecs, setReroofSpecs] = useState([]);
  const [rebuilding, setRebuilding] = useState(null); // 'clear' | 'diagram' | spec_id | null
  // iter303ak-fix24 · Child appraisal detection. When this Components
  // tab is opened for a child (parent_appraisal_id set), we expose
  // extra buttons to Pull-from-Builder and Clear auto-imported rows.
  const [isChildAppraisal, setIsChildAppraisal] = useState(false);
  const [childAppraisalMeta, setChildAppraisalMeta] = useState(null);
  const [childActionBusy, setChildActionBusy] = useState(null); // 'pull' | 'clear' | null
  useEffect(() => {
    if (!appraisalId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/appraisals/${appraisalId}`);
        if (cancelled) return;
        const parentId = data?.parent_appraisal_id || '';
        if (parentId) {
          setIsChildAppraisal(true);
          setChildAppraisalMeta({
            appraisal_no: data?.appraisal_no,
            quote_type_ref: data?.quote_type_ref,
            variant_label: data?.variant_label,
            auto_pushed_at: data?.components_auto_pushed_at,
            auto_push_result: data?.components_auto_push_result,
            builder_origin: data?.builder_origin || data?.draft_builder || '',
            quotation_type: data?.quotation_type || '',
          });
        } else {
          setIsChildAppraisal(false);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [appraisalId, api, refreshKey]);
  // iter303ak-fix24 · Pull-from-builder handler (child only)
  const handlePullFromChildBuilder = async () => {
    if (!appraisalId || childActionBusy) return;
    if (!window.confirm('Pull the latest builder data for this quote?\n\nExisting auto-imported rows with the same source will be replaced. Manual rows you typed by hand are never touched.')) return;
    setChildActionBusy('pull');
    try {
      const { data } = await api.post(`/appraisals/${appraisalId}/components/pull-from-child-builder`);
      if (data?.skipped) {
        toast.warning(`Pull skipped — ${data?.error || 'no builder data available'}`);
      } else {
        toast.success(`Pulled ${data?.inserted || 0} lines from ${data?.source || 'builder'}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pull failed');
    } finally {
      setChildActionBusy(null);
    }
  };
  // iter303ak-fix24 · Clear auto-imported handler (child only)
  const handleClearAutoImported = async () => {
    if (!appraisalId || childActionBusy) return;
    if (!window.confirm('Clear ALL auto-imported component_lines on this quote?\n\nManual rows you typed by hand will survive. This cannot be undone (except by re-pulling from the builder).')) return;
    setChildActionBusy('clear');
    try {
      const { data } = await api.post(`/appraisals/${appraisalId}/components/clear-auto`);
      toast.success(`Cleared ${data?.deleted || 0} auto-imported line${(data?.deleted || 0) === 1 ? '' : 's'}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clear failed');
    } finally {
      setChildActionBusy(null);
    }
  };
  // Iter229 — Materials Order List modal toggle. Opens a supplier-ready
  // aggregation of every Ridge / Valley / Barge / Flashing line through
  // the 9 m mill max + 200 mm lap rule.
  const [materialsOrderOpen, setMaterialsOrderOpen] = useState(false);

  // Per-component-type Profile dropdowns (loaded once)
  const [profileLists, setProfileLists] = useState({});

  // Roof material context — populated from the property's first roof_detail
  // so we can auto-narrow the Flashing dropdown (1,187 → ~190 options) to the
  // material the inspector actually needs. The same picker is now applied
  // to Sheet / Ridge / Valley / Barge — each section keeps its own
  // selection so the operator can mix (e.g. Galv ridges + Maxx flashings).
  const [roofType, setRoofType] = useState('');
  // Iter132 — keep Roof Brand + Profile in state so the Auto-suggest /
  // Apply-to-all cascade can hint at the right rate-row name (e.g.
  // "Bed and Point Modern" when subtype = Modern + Bed & Point).
  const [roofBrand, setRoofBrand] = useState('');
  const [roofProfile, setRoofProfile] = useState('');
  // Iter189 — section-level Sheet profile picker. Defaults to the
  // appraisal's roof_details.roof_profile so the operator typically
  // doesn't need to touch it. Cascade-button next to the picker
  // stamps the chosen profile onto every Sheet row's `profile` field
  // AND narrows that section's Type dropdown to rate rows whose
  // type_name contains the profile keyword (e.g. "Trimrib").
  const [sheetSectionProfile, setSheetSectionProfile] = useState('');
  // Iter146 — Material Type from Roof Details (Iron family: Galv / Maxx
  // / Zcore / Zinc / Aluguard / ZinaCore / MagnaFlow / Alu / Copper).
  // Drives the cascade narrow for the 5 Iron-family sections (Sheet,
  // Ridge, Valley, Barge, Flashing). Blank for tile / concrete roofs.
  const [materialType, setMaterialType] = useState('');
  // Iter152 — Fastenings Units source. roofM2 = roof_details.roof_m2.
  // facePinLengths = sorted desc list of all Face pins from the
  // annotated diagram (length_m). Both fields drive the Fastenings
  // section auto-fill (Qty=1, Units=Roof m²) + supplier-order
  // calculation (ScrewQty = Units × ScrewRate).
  const [roofM2, setRoofM2] = useState(0);
  // Iter235 — cap cascade. The appraisal carries roof_subtype (e.g.
  // 'Concrete Tile' / 'Clay Tile' / 'Tin Tile' / 'Iron') and an
  // optional tile_profile_override (e.g. 'Monier Std' / 'Old Clay' /
  // 'Decramastic'). These are DISTINCT from the property's
  // roof_type/roof_profile (which we already load for other cascades),
  // so we hydrate them separately and feed them straight to CapPanel.
  const [roofSubtype, setRoofSubtype] = useState('');
  const [tileProfileOverride, setTileProfileOverride] = useState('');
  // Iter155 — capture the appraisal's roof colour so Push to BOM can
  // stamp it on auto-generated Rivet rows (operator can override via
  // a dropdown on each Rivet row).
  const [roofColour, setRoofColour] = useState('');
  const [facePinLengths, setFacePinLengths] = useState([]); // [{id, length_m, label}]
  // Iter153 — Flashings template library. Loaded once with the rate
  // cards. Each Flashing row picks a `flashing_type` (category, e.g.
  // "Back Tray") and then a specific template; girth_mm + folds_qty
  // are auto-derived from the template's folds[] and seed the rate
  // cascade via {Material} {Girth}W/{Folds}F substring matching.
  const [flashingTemplates, setFlashingTemplates] = useState([]);
  // Iter247 — Valley template fold-signatures driving the Valley
  // row's Profile dropdown. Each entry: {signature, template_id,
  // template_name, bend_count, girth_mm, start_angle_deg, folds[]}.
  const [valleyProfileOptions, setValleyProfileOptions] = useState([]);
  // Iter250 — Barge template fold-signatures driving the Barge row's
  // Profile dropdown. Same shape as valleyProfileOptions, sourced
  // from /flashing-templates/barge-profile-options (which already
  // excludes the soft-deleted specialty templates).
  const [bargeProfileOptions, setBargeProfileOptions] = useState([]);
  const [materialBySection, setMaterialBySection] = useState({});  // { Flashing: 'Galv', Ridge: 'Concrete', … }
  // Iter147 — Section-level "Show all rates" override. Bypasses the
  // cascade-link narrow + material narrow for every row in that
  // section, so the operator can pick from the full catalogue (e.g.
  // a one-off MagnaFlow Ridge on a Zinc Iron roof). Set per work-type
  // key. UI-only state — not persisted. Per-row override lives in
  // `ComponentLineRow` (each row can flip its own bypass independently).
  const [sectionShowAll, setSectionShowAll] = useState({}); // { Ridge: true, Valley: false, … }
  // Iter142 — Gutter Builder context piped up from the Spouting section's
  // Gutter Details bar. The Spouting Type dropdown narrows its options
  // based on this {material, profile, gutterType} so the operator sees a
  // shortlist matching their gutter pick instead of all 39 rate rows.
  const [gutterCtx, setGutterCtx] = useState(null);
  // Persisted "Primary material" choice for this appraisal. When set, it
  // drives the master picker highlight regardless of whether every
  // section can actually use it (e.g. flashings stocked only in Galv —
  // the section stays on its current rate-card material, but the
  // Primary badge still reads Maxx). Loaded from `appraisal.primary_material`
  // on mount; PATCHed back on every cascade so the choice survives
  // page reloads.
  const [primaryMaterial, setPrimaryMaterial] = useState(null);
  // Persisted Quotation-Type ref for this appraisal (e.g. "LongRun Iron
  // Reroof"). Drives the new "Quotation Type" picker above Primary
  // Material — selecting one cascades the row's `roof_type` to Primary
  // Material + every per-section material filter, narrowing Component
  // pickers to the right material family.
  const [quoteType, setQuoteType] = useState('');
  const [quoteTypes, setQuoteTypes] = useState([]); // [{ref, roof_type, quote_category}]
  // Iter316 · Phase 2 · Quotation Type cascade filter. Narrows the
  // ~42-row dropdown down to the shortlist matching the appraisal's
  // roof_type AND (optionally) the operator-picked service_option.
  // Roof-type filter is automatic (from `roofType` state — set by
  // roof_details loader); service_option is a chip picker above the
  // dropdown. `''` = "show all service_options" (no filter).

  // C4-P1.1 / P1.2 / P1.3 (2026-02c) — anchors required by ADV-Marine,
  // ADV-Photo, and R-D-007/008 rules. Sourced from the appraisal
  // envelope in `load()` (service_option is back-synced from
  // quote_type_ref by the backend per Iter211) and from a lightweight
  // photo-count fetch alongside the other component loads. Without
  // these anchors the rules were seeded but never fired.
  const [serviceOption, setServiceOption] = useState('');
  const [environment, setEnvironment] = useState('');
  const [photoCount, setPhotoCount] = useState(0);

  // C4-audit BAY wiring (2026-02c) — compute `has_unlinked_dektites`
  // from the current Aquaseal group. A dektite is "unlinked" when the
  // row has no `linked_backtray` payload AND is not the synthetic
  // `[AUTO:extra_clips]` accessory row (those aren't dektites, so they
  // don't need pairings). Passed to `useCascadeEngine` as an anchor so
  // BAY-Unlinked-Dektite-Advisory fires whenever any real dektite is
  // still missing its paired back-tray flashing.
  const hasUnlinkedDektites = useMemo(() => {
    const aq = groups.Aquaseal || [];
    return aq.some(l => {
      const isExtraClips = /\[AUTO:extra_clips\]/i.test((l && l.notes) || '');
      if (isExtraClips) return false;
      return !l?.linked_backtray;
    });
  }, [groups.Aquaseal]);

  // C3a-γ / C3c-γ · backend cascade engine (Phase C rules engine).
  // Fires ONCE per unique (roofType, roofBrand, roofProfile, quoteType,
  // gutter_material) tuple. `engineMaterial` supersedes the local
  // helpers.js inference · falls back to helpers.js when null / loading.
  // C3c-γ · additional anchors: `gutter_material` narrows Spouting picks
  // once the Gutter Details bar canonicalises the material choice.
  // C4-P1 (2026-02c) · additional anchors: `service_option` unblocks
  // R-D-007/008 (repairs carry-through + section preselect),
  // `environment` unblocks ADV-Marine (Coloursteel hard-gate),
  // `photo_count` unblocks ADV-Photo (completeness warning), and
  // `material` feeds ADV-Marine's second clause.
  const {
    engineMaterial,
    engineRatePicks,
    validationErrors,
    hasBlockingErrors,
    // C4h (2026-02c) — telemetry for the EngineStatusBadge.
    loading: engineLoading,
    rulesFired,
    rulesEvaluated,
    engineVersionHash,
  } = useCascadeEngine({
    roof_type:      roofType,
    brand:          roofBrand,
    profile:        roofProfile,
    quote_type:     quoteType,
    gutter_material: gutterCtx?.material || '',
    service_option:  serviceOption,
    environment:     environment,
    photo_count:     photoCount,
    material:        primaryMaterial || '',
    has_unlinked_dektites: hasUnlinkedDektites,
  });

  const flashingMaterial = materialBySection.Flashing || 'all';
  const setFlashingMaterial = (v) => setMaterialBySection(s => ({ ...s, Flashing: v }));

  // Sheet has a structured map { "Corrugate": [0.76, 1.00], "MaxiSpan": [0.91], ... }
  // so we can render plain profile names + a smart Cover field.
  const sheetProfileMap = useMemo(() => buildSheetProfileMap(profileLists.Sheet || []), [profileLists]);
  const sheetProfileNames = useMemo(() => Object.keys(sheetProfileMap), [sheetProfileMap]);
  // Phase C · C4c (2026-02c) — Sheet profile cascade extracted to
  // ./AppraisalComponents/hooks/useSheetProfileCascade.js
  useSheetProfileCascade({
    sheetSectionProfile,
    setSheetSectionProfile,
    sheetProfileNames,
    groupsSheetLength: (groups.Sheet || []).length,
    setGroups,
  });

  // initial load — lines + rate cards in parallel
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [linesRes, ddRes, apRes, qtRes, ftRes] = await Promise.all([
        api.get(`/appraisals/${appraisalId}/component-lines`),
        api.get('/dropdowns'),
        api.get(`/appraisals/${appraisalId}`).catch(() => ({ data: null })),
        api.get('/quote-types').catch(() => ({ data: [] })),
        api.get('/flashing-templates').catch(() => ({ data: [] })),
      ]);
      setQuoteTypes(Array.isArray(qtRes.data) ? qtRes.data : []);
      setFlashingTemplates(Array.isArray(ftRes.data) ? ftRes.data : []);
      // Iter247 — Valley profile dropdown driver. Fetched once per
      // load. Quiet failure → empty array → row falls back to the
      // free-text input.
      api.get('/flashing-templates/valley-profile-options')
        .then(r => setValleyProfileOptions(Array.isArray(r.data) ? r.data : []))
        .catch(() => setValleyProfileOptions([]));
      // Iter250 — Barge profile dropdown driver (mirrors Valley).
      api.get('/flashing-templates/barge-profile-options')
        .then(r => setBargeProfileOptions(Array.isArray(r.data) ? r.data : []))
        .catch(() => setBargeProfileOptions([]));
      // Hydrate the persisted Quotation Type choice (if any). Reads
      // the canonical `quote_type_ref` — the old `quote_type` alias
      // has been backfilled & unset across the collection.
      const persistedQt = (apRes?.data?.quote_type_ref || '').trim();
      if (persistedQt) setQuoteType(persistedQt);
      // C4-P1.3 (2026-02c) — hydrate `service_option` for the engine
      // (backend already back-syncs from quote_type_ref per Iter211).
      setServiceOption(String(apRes?.data?.service_option || '').trim());
      // C4-P1.1 (2026-02c) — hydrate `environment` for ADV-Marine.
      // Prefer the operator-confirmed `environment_final`; fall back
      // to the auto-suggested value from the property zone lookup.
      setEnvironment(
        String(apRes?.data?.environment_final || apRes?.data?.environment_suggested || '').trim()
      );
      // C4-P1.2 (2026-02c) — hydrate `photo_count` for ADV-Photo.
      // Fires a lightweight list call · array length becomes the
      // anchor (advisory only warns when count < 3). Silent-fail so
      // Components tab still loads if photos endpoint is offline.
      api.get(`/photos/${appraisalId}`)
        .then(r => setPhotoCount(Array.isArray(r.data) ? r.data.length : 0))
        .catch(() => setPhotoCount(0));
      // Pull the property's first roof_type so we can auto-default the
      // Flashing material picker. Falls back gracefully if there's no roof.
      const propId = apRes?.data?.property_id;
      // Hydrate the persisted Primary material choice (if any). Loaded
      // BEFORE the auto-derive useEffect kicks in so the operator's
      // saved value beats the roof-type guess.
      const persistedPrimary = (apRes?.data?.primary_material || '').trim();
      if (persistedPrimary) {
        setPrimaryMaterial(persistedPrimary);
      }
      // Iter235 — hydrate the cap-cascade context straight from the
      // appraisal payload (NOT the property's /roof-details which
      // carries different vocabulary).
      setRoofSubtype(String(apRes?.data?.roof_subtype || '').trim());
      setTileProfileOverride(String(apRes?.data?.tile_profile_override || '').trim());
      // Iter243 — keep a local copy of roof_type alongside the state
      // setter. React state updates are async, so the profile-list
      // builder below would otherwise read the stale previous value
      // and never filter the Ridge dropdown on first render.
      let localRoofType = '';
      // Iter316 · P0-3 Fastening m² fix (2026-02c) — read every roof
      // detail row (not just the first) so multi-roof properties add
      // up correctly. Was `rdRes.data[0]` only → half the property's
      // area got billed. `resolve_roof_m2`'s Source B behaviour.
      let roofDetailsAreaSum = 0;
      if (propId) {
        try {
          const rdRes = await api.get(`/roof-details/${propId}`);
          const rdList = Array.isArray(rdRes.data) ? rdRes.data : [];
          const rd0 = rdList[0] || {};
          roofDetailsAreaSum = rdList.reduce((s, r) => s + (Number(r?.roof_m2) || 0), 0);
          const rt = rd0.roof_type || '';
          if (rt) { setRoofType(rt); localRoofType = rt; }
          if (rd0.roof_brand)   setRoofBrand(rd0.roof_brand);
          if (rd0.roof_profile) {
            setRoofProfile(rd0.roof_profile);
            // Iter189 — normalise the inherited profile to the case used
            // in the rate catalogue so the section picker preselects
            // correctly (operator types "Trimrib" in roof details but
            // the rate names use "TrimRib").
            setSheetSectionProfile(rd0.roof_profile);
          }
          if (rd0.material_type) setMaterialType(rd0.material_type);
          // Iter155 — capture roof_colour for the Push-to-BOM rivet
          // colour stamp (Misc section auto-row).
          setRoofColour(String(rd0.roof_colour || '').trim());
        } catch (e) { /* no roof yet */ }
      }
      // Iter152 — capture Face pins from the diagram. Used as an
      // alternate Units source on Fastening rows (operator can flip
      // m² source from Roof Details → Face Pin / Sum of Face Pins).
      const anns = Array.isArray(apRes?.data?.roof_diagram_annotations) ? apRes.data.roof_diagram_annotations : [];
      const faces = anns
        .filter(a => String(a?.type || '').toLowerCase() === 'face' && Number(a?.length_m) > 0)
        // Dedupe by (slot, length_m, x, y) so the same Face annotation
        // copied across roofer/painter/concrete_restore slots isn't
        // counted multiple times.
        .reduce((acc, a) => {
          const k = `${Number(a.length_m).toFixed(2)}|${Number(a.x_pct).toFixed(1)}|${Number(a.y_pct).toFixed(1)}`;
          if (!acc._seen.has(k)) { acc._seen.add(k); acc.list.push(a); }
          return acc;
        }, { _seen: new Set(), list: [] }).list
        .map(a => ({ id: a.id, length_m: Number(a.length_m), label: a.label || '' }))
        .sort((a, b) => b.length_m - a.length_m);
      setFacePinLengths(faces);
      // Iter316 · P0-3 Fastening m² fix — pick the canonical face
      // area for the Fastening Units cascade. Priority mirrors the
      // backend `resolve_roof_m2` resolver:
      //   1. Face-pin sum from the diagram (highest confidence · the
      //      annotator's "Face 250.0 m²" summary total). Face pins
      //      store their m² in `length_m` (see `_sum_face_pins` in
      //      `services/roof_details_view.py` — same field, same
      //      priority ladder).
      //   2. Sum of roof_details.roof_m2 across every roof on the
      //      property (multi-roof properties).
      //   3. Zero (no data yet).
      // Was: `setRoofM2(rd0.roof_m2)` — first roof only → wrong on
      // multi-roof + wrong when face pins carry the truth.
      const facePinAreaSum = faces.reduce((s, f) => s + (Number(f?.length_m) || 0), 0);
      setRoofM2(facePinAreaSum > 0 ? facePinAreaSum : roofDetailsAreaSum);
      const g = {};
      WORK_TYPES.forEach(wt => {
        g[wt.key] = (linesRes.data.groups[wt.key] || []).map(l => {
          // Wave 3 M1 fix (2026-02) — prefer the STORED `_key` (round-
          // tripped from the backend via the gatekeeper) BEFORE falling
          // back to `_id` or a fresh UUID. Without this, a client-side
          // `_key` assigned to a newly-added Sheet row was silently
          // replaced by the backend's ObjectId on next reload — which
          // broke every Underlay row's `replacement_sheet_keys`
          // reference (they still pointed at the old client `_key`).
          const seed = { ...l, _key: l._key || l._id || crypto.randomUUID() };
          // Derive material from existing type_name for legacy Flashing lines
          // that pre-date this feature, so the per-line dropdown narrows
          // immediately without forcing the user to re-pick.
          //
          // Iter225 — Use the canonical `detectMaterialFor` helper instead
          // of a naive head-split. The previous head=split-first-token
          // approach turned pin-stub type_names like 'Flashing F2' into
          // the bogus material 'Flashing', which then short-circuited
          // every downstream cascade. `detectMaterialFor` walks the
          // section's known token list and returns null when nothing
          // matches, letting ComponentLineRow's row-local cascade fire.
          if (wt.key === 'Flashing' && !seed.material && seed.type_name) {
            const detected = detectMaterialFor('Flashing', seed.type_name);
            if (detected) seed.material = detected;
          }
          return seed;
        });
      });
      setGroups(g);
      const wtKeys = new Set(WORK_TYPES.map(wt => wt.key));
      setOtherLines(Object.entries(linesRes.data.groups || {})
        .filter(([k]) => !wtKeys.has(k))
        .flatMap(([, arr]) => arr || []));
      // Iter132 — clear the auto-fill guard so the cascade re-runs on
      // the freshly loaded set. Critical for the Clear & Rebuild →
      // Push to Components flow where the previous run already
      // flipped the flag.
      setAutoFilledOnce(false);
      // Build per-component profile map: { Cleaning: [...], Travel: [...], ... }
      const dd = ddRes.data || {};
      const pMap = {};
      WORK_TYPES.forEach(wt => {
        const cat = `${wt.key.toLowerCase()}_profile`;
        if (Array.isArray(dd[cat]) && dd[cat].length) pMap[wt.key] = dd[cat];
      });
      // Iter243 — Ridge Profile dropdown narrows to the appraisal's
      // roof type. Iron roofs only show RoundTop Std/Wide, Concrete
      // shows the Monier/Old/Ross set, Tin shows Decramastic, etc.
      // Driven by the `roof_kinds` metadata on each profile (admin
      // overridable via PUT /api/dropdowns/ridge_profile/meta).
      try {
        const rpRes = await api.get('/dropdowns/ridge_profile/with-meta');
        const meta = Array.isArray(rpRes.data) ? rpRes.data : [];
        if (meta.length) {
          const rtLower = String(localRoofType || '').toLowerCase().trim();
          let kind = '';
          if (/iron|long\s*run|longrun|short\s*run/i.test(rtLower)) kind = 'iron';
          else if (/tin|decra|colortile|color\s*tile/i.test(rtLower)) kind = 'tin';
          else if (/clay/i.test(rtLower)) kind = 'clay';
          else if (/concrete/i.test(rtLower)) kind = 'concrete';
          // When the roof type is set, keep only profiles whose
          // roof_kinds includes that kind. Otherwise leave the full
          // list visible so the operator isn't blocked.
          pMap.Ridge = kind
            ? meta.filter(m => Array.isArray(m.roof_kinds) && m.roof_kinds.includes(kind)).map(m => m.label)
            : meta.map(m => m.label);
        }
      } catch { /* fallback to flat list */ }
      setProfileLists(pMap);
      // Open the work types that already have lines
      const open = new Set();
      Object.entries(g).forEach(([k, arr]) => { if (arr.length > 0) open.add(k); });
      setOpenTypes(open);
      // Pre-fetch the rate-card rows for each work type that has lines (lazy-load others on first open)
      const initRates = {};
      const wtNeed = Array.from(open);
      const ratePromises = wtNeed.map(wt => api.get(`/component-rates?component_type=${encodeURIComponent(wt)}`).then(r => [wt, r.data]).catch(() => [wt, []]));
      (await Promise.all(ratePromises)).forEach(([wt, list]) => { initRates[wt] = list; });
      setRates(initRates);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, appraisalId]);

  // Iter229z — `refreshKey` is bumped by the parent when the
  // RoofDiagramAnnotator's "Push to Components" fires successfully,
  // so the tab re-fetches its line items without the operator having
  // to switch tabs and come back.
  useEffect(() => { load(); }, [load, refreshKey]);

  // Phase C · C4c (2026-02c) — Underlay cascades (retro-seed + roll-qty)
  // extracted to ./AppraisalComponents/hooks/useUnderlayCascade.js
  useUnderlayCascade({
    rates,
    roofM2,
    groupsUnderlay: groups.Underlay,
    engineRatePicks,
    setGroups,
  });

  // Iter316 · P0-1 (2026-02c) — Flashing rate cascade. When a diagram
  // Flashing pin pushes with girth_mm + folds_qty + material set, this
  // hook auto-picks the smallest catalogue rate row where width ≥ girth
  // AND the SKU name matches material + `${bends}F`. Mirrors the
  // useUnderlayCascade pattern — guarded by `type_auto_filled` so an
  // operator manual pick survives a subsequent diagram re-push.
  useFlashingRateCascade({
    rates,
    groupsFlashing: groups.Flashing,
    primaryMaterial,
    setGroups,
  });

  // Phase C · C4g (2026-02c) — Row-scoped repairs material carry-through.
  // Fires for repair-family quote types · stamps engineMaterial onto
  // rows that lack one across every material-aware section, gated by
  // each section's rate-catalogue support.
  useRepairsRowCascade({
    loading,
    quoteType,
    engineMaterial,
    rates,
    groups,
    setGroups,
  });

  const ensureRates = useCallback(async (wt) => {
    if (rates[wt]) return rates[wt];
    try {
      const { data } = await api.get(`/component-rates?component_type=${encodeURIComponent(wt)}`);
      setRates(prev => ({ ...prev, [wt]: data }));
      return data;
    } catch (e) { return []; }
  }, [api, rates]);

  // A line counts as "real" if it has a Type selected or a positive quantity.
  // Empty placeholder rows don't.
  const hasRealData = (line) => Boolean(line && (String(line.type_name || '').trim() || Number(line.quantity) > 0));

  const toggleOpen = (wt) => {
    const wasOpen = openTypes.has(wt);

    if (wasOpen) {
      // Manual collapse — preserve whatever data is in the section
      setOpenTypes(prev => {
        const next = new Set(prev);
        next.delete(wt);
        return next;
      });
      return;
    }

    // Opening: auto-close any other open section that has no real data and
    // strip out its empty placeholder rows.
    const toClose = [];
    openTypes.forEach(other => {
      if (other === wt) return;
      const hasReal = (groups[other] || []).some(hasRealData);
      if (!hasReal) toClose.push(other);
    });

    if (toClose.length) {
      setGroups(prev => {
        const updated = { ...prev };
        toClose.forEach(c => { updated[c] = (updated[c] || []).filter(hasRealData); });
        return updated;
      });
    }

    setOpenTypes(prev => {
      const next = new Set(prev);
      toClose.forEach(c => next.delete(c));
      next.add(wt);
      return next;
    });
    ensureRates(wt);

    // Auto-add a fresh empty line so the user can start typing immediately.
    // Silent (no dirty flag) — only real edits should mark the form dirty.
    if ((groups[wt] || []).length === 0) addLine(wt, { silent: true });
  };

  const allOpen = openTypes.size === WORK_TYPES.length;
  const openAll = () => {
    if (allOpen) { setOpenTypes(new Set()); return; }
    const all = new Set(WORK_TYPES.map(w => w.key));
    setOpenTypes(all);
    WORK_TYPES.forEach(w => ensureRates(w.key));
  };

  // Iter257 — Fast-menu jump. Expands the target section (if collapsed)
  // and scrolls its card into view. Used by the sticky chip bar at the
  // top of the Components tab AND the per-section "↑ Menu" buttons (the
  // latter just scrolls the chip bar back into view instead). Section
  // expansion reuses toggleOpen's auto-close-empty behaviour so jumping
  // from a section with a stray blank row cleans up after itself.
  const jumpToSection = (wt) => {
    if (!openTypes.has(wt)) {
      // Expand without going through toggleOpen so we never accidentally
      // collapse an already-open target — but reuse the empty-strip side
      // effect so the operator's view stays tidy.
      const toClose = [];
      openTypes.forEach(other => {
        if (other === wt) return;
        const hasReal = (groups[other] || []).some(hasRealData);
        if (!hasReal) toClose.push(other);
      });
      if (toClose.length) {
        setGroups(prev => {
          const updated = { ...prev };
          toClose.forEach(c => { updated[c] = (updated[c] || []).filter(hasRealData); });
          return updated;
        });
      }
      setOpenTypes(prev => {
        const next = new Set(prev);
        toClose.forEach(c => next.delete(c));
        next.add(wt);
        return next;
      });
      ensureRates(wt);
      if ((groups[wt] || []).length === 0) addLine(wt, { silent: true });
    }
    // Let React paint the newly-expanded section before scrolling.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="comp-section-${wt}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const scrollToFastMenu = () => {
    // Iter257b — The Components tab scrolls within a `<main>` element
    // (overflow-y-auto), NOT the window. scrollIntoView auto-targets
    // the nearest scrolling ancestor, so we anchor to the wrapper's
    // top — that puts the Roof Details Adjustments card + the sticky
    // chip bar right below it at the visible top of main, giving the
    // operator a clear "back to the menu" landmark.
    const wrapper = document.querySelector('[data-testid="components-tab-wrapper"]');
    if (wrapper) wrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const jumpToDiagram = () => {
    // Iter257b — The RoofDiagramAnnotator is rendered in the appraisal
    // page header (always present regardless of active tab). Scroll
    // it into view directly; no tab switch needed. Fall back to
    // scrolling to the top of the scrolling container when the
    // annotator is hidden (e.g. no template picked yet) so the
    // operator at least sees the page header.
    const annot = document.querySelector('[data-testid="roof-diagram-annotator"]');
    if (annot) {
      annot.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const mainEl = document.querySelector('main');
    if (mainEl) mainEl.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const BLANK_LINE = (wt) => ({
    component_type: wt,
    type_name: '',
    // Iter138 — inherit the Roof Details profile (e.g. 'Centurian') so
    // a freshly-added Tile / Ridge / Sheet line lands on the right
    // profile shortlist without forcing the operator to pick it.
    profile: MATERIAL_SECTIONS.includes(wt) ? (roofProfile || '') : '',
    cover_width: 0, face_length: 0,
    quantity: wt === 'Flashing' ? 1 : 0, length: 0,
    cap_count: 0, cap_length_m: 0, cap_type: '',
    buy_cost: 0, materials_rate: 0, labour_rate: 0, contractor_rate: 0,
    supplier: '', supplier_code: '', materials_desc: '', labour_details: '',
    notes: '', override: false, folds: [], start_angle_deg: 0,
    // Material (Galv / Maxx / Zcore / Zinc / Alu / Aluguard / Concrete / …).
    // The per-line dropdown narrows automatically; auto-stamped from the
    // section's pill-bar default on creation. Generalised 2026-02-17 to
    // cover Sheet / Ridge / Valley / Barge / Flashing.
    material: MATERIAL_SECTIONS.includes(wt) && (materialBySection[wt] || 'all') !== 'all'
      ? materialBySection[wt]
      : '',
  });

  const addLine = (wt, { silent = false } = {}) => {
    // Iter190+ — pre-compute Sheet-row defaults so new Sh# rows inherit
    // both the section profile AND a sensible cover, so the operator
    // only needs to fill qty/length (the "cascade by materials and
    // profile" the operator asked for in the walk-through).
    let sheetSeed = {};
    if (wt === 'Sheet' && sheetSectionProfile) {
      sheetSeed.profile = sheetSectionProfile;
      // Derive cover from the SECTION's rate rows (sheetProfileMap is
      // populated from bare profile labels which carry no cover, so
      // we have to scan the rate card itself).
      const sectionRates = rates.Sheet || [];
      const seen = new Set();
      sectionRates.forEach(r => {
        const tn = String(r?.type_name || '');
        if (!tn.toLowerCase().includes(sheetSectionProfile.toLowerCase())) return;
        const mm = tn.match(/(\d{3,4})\s*mm/i);
        if (mm) seen.add(parseFloat((parseInt(mm[1], 10) / 1000).toFixed(3)));
      });
      const covers = Array.from(seen).sort((a, b) => a - b);
      if (covers.length) sheetSeed.cover_width = covers[covers.length - 1];
    }
    // Iter253 — Underlay rows auto-seed with the default rate (Covertek
    // 215 SS 50m²) AND compute the required roll count from roof_m2 in
    // one shot, so the operator never has to manually figure out
    // "how many rolls". Operator can override Type or qty afterwards.
    // Iter255 — Safety row defaults. Edge Protection type pre-picked
    // (operator can change), Pro Platform Limited supplier seeded,
    // is_estimation flag set true so the EST badge surfaces until
    // confirmed pricing arrives.
    if (wt === 'Safety') {
      const safetyRates = rates.Safety || [];
      const defaultRate = safetyRates.find(r =>
        String(r.type_name || '').toLowerCase() === 'edge protection'
      );
      if (defaultRate) {
        sheetSeed.type_name      = defaultRate.type_name;
        sheetSeed.buy_cost       = Number(defaultRate.buy_cost)       || 0;
        sheetSeed.materials_rate = Number(defaultRate.materials_rate) || 0;
        sheetSeed.labour_rate    = Number(defaultRate.labour_rate)    || 0;
        sheetSeed.contractor_rate= Number(defaultRate.contractor_rate)|| 0;
        sheetSeed.supplier       = defaultRate.supplier || '';
        sheetSeed.supplier_code  = defaultRate.supplier_code || '';
      }
      sheetSeed.safety_scope = 'roofing';
      sheetSeed.is_estimation = true;
      sheetSeed.scaffold_supplier_name  = 'Pro Platform Limited';
      sheetSeed.scaffold_supplier_email = 'proplatformltd@gmail.com';
    }
    if (wt === 'Underlay') {
      const underlayRates = rates.Underlay || [];
      const defaultRate = underlayRates.find(r =>
        String(r.type_name || '').toLowerCase().startsWith('covertek 215')
      ) || underlayRates.find(r => Number(r.roll_coverage_m2) > 0);
      if (defaultRate) {
        sheetSeed.type_name      = defaultRate.type_name;
        sheetSeed.buy_cost       = Number(defaultRate.buy_cost)       || 0;
        sheetSeed.materials_rate = Number(defaultRate.materials_rate) || 0;
        sheetSeed.labour_rate    = Number(defaultRate.labour_rate)    || 0;
        sheetSeed.contractor_rate= Number(defaultRate.contractor_rate)|| 0;
        sheetSeed.supplier       = defaultRate.supplier || '';
        sheetSeed.supplier_code  = defaultRate.supplier_code || '';
        sheetSeed.materials_desc = defaultRate.materials_desc || '';
        sheetSeed.labour_details = defaultRate.labour_details || '';
        const coverage = Number(defaultRate.roll_coverage_m2) || 0;
        const m2 = Number(roofM2) || 0;
        if (coverage > 0 && m2 > 0) {
          // ceil(roof_m2 / coverage) — round UP so we always have
          // enough underlay; never round down (could leave a gap).
          sheetSeed.quantity = Math.ceil(m2 / coverage);
          sheetSeed.qty_auto_filled = true;
          sheetSeed.qty_auto_source_m2 = m2;
        }
      }
    }
    setGroups(prev => ({
      ...prev,
      [wt]: [...(prev[wt] || []), {
        _key: crypto.randomUUID(),
        ...BLANK_LINE(wt),
        ...sheetSeed,
      }],
    }));
    // Re-fire the cascade auto-fill so the freshly-added line picks up
    // its cascade-linked Type / Material from the roof_type + brand +
    // profile context (iter138). Without this the line lands blank and
    // the operator has to scroll the dropdown by hand.
    if (MATERIAL_SECTIONS.includes(wt)) setAutoFilledOnce(false);
    if (!silent) setDirty(true);
  };

  const duplicateLastLine = (wt) => {
    setGroups(prev => {
      const existing = prev[wt] || [];
      const last = existing[existing.length - 1];
      if (!last) return prev;
      const seed = {
        component_type: wt,
        type_name: last.type_name || '',
        profile: last.profile || '',
        cover_width: last.cover_width || 0,
        face_length: last.face_length || 0,
        cap_count: last.cap_count || 0,
        cap_length_m: last.cap_length_m || 0,
        cap_type: last.cap_type || '',
        quantity: last.quantity || 0,
        length: last.length || 0,
        buy_cost: last.buy_cost || 0,
        materials_rate: last.materials_rate || 0,
        labour_rate: last.labour_rate || 0,
        contractor_rate: last.contractor_rate || 0,
        supplier: last.supplier || '',
        supplier_code: last.supplier_code || '',
        materials_desc: last.materials_desc || '',
        labour_details: last.labour_details || '',
        notes: last.notes || '',
        override: last.override || false,
        folds: Array.isArray(last.folds) ? last.folds.map(f => ({ ...f })) : [],
        start_angle_deg: last.start_angle_deg ?? 0,
        material: last.material || '',
      };
      return { ...prev, [wt]: [...existing, { _key: crypto.randomUUID(), ...seed }] };
    });
    setDirty(true);
  };

  const removeLine = (wt, key) => {
    const row = groups[wt]?.find(l => l._key === key);
    if (!row) return;
    if (!isAdmin && Number(row.quantity) > 0) {
      if (!confirm('Remove this line?')) return;
    }
    setGroups(prev => {
      const next = { ...prev };
      // 1. Remove the target row from its section.
      next[wt] = (prev[wt] || []).filter(l => l._key !== key);

      // ── Wave 2.7 (2026-02) · C4d (2026-02c) — cross-section bay-tray
      // link cascade extracted to
      //   ./AppraisalComponents/helpers/baytrayLinkCascade.js
      // Sets up the future `row_link` engine primitive · the helper is
      // a pure fn returning a delta, so the engine can one day emit
      // the same shape declaratively from BAY-* rules without touching
      // this caller.
      if (wt === 'Flashing') {
        Object.assign(next, cascadeDeleteFlashingToAquaseal(prev, row));
      }
      if (wt === 'Aquaseal') {
        Object.assign(next, cascadeDeleteAquasealToFlashing(prev, row));
      }

      return next;
    });
    setDirty(true);
  };

  const updateLine = (wt, key, patch) => {
    // Iter253 — Manual qty edit on an Underlay row flips qty_auto_filled
    // to false so the roof_m2-change cascade stops overwriting the
    // operator's number. The Reset button explicitly re-asserts the
    // flag in its own patch.
    let finalPatch = patch;
    if (wt === 'Underlay'
        && Object.prototype.hasOwnProperty.call(patch, 'quantity')
        && !Object.prototype.hasOwnProperty.call(patch, 'qty_auto_filled')) {
      finalPatch = { ...patch, qty_auto_filled: false };
    }
    setGroups(prev => ({ ...prev, [wt]: prev[wt].map(l => l._key === key ? { ...l, ...finalPatch } : l) }));
    setDirty(true);
  };

  // Iter132 — "Apply to all R# refs" cascade. Pushes one field's value
  // from a single line out to every sibling in the same component-type
  // group. Skips the source line and never touches rows where the
  // operator has already typed a different value (only overwrites blank
  // or `auto`-flagged rows by default; pass `force` to overwrite).
  //
  // Iter147 — when the cascaded field is `type_name`, also stamp the
  // rate-$ + supplier + material/profile fields from the matching rate
  // card onto each touched sibling. Mirrors the auto-fill cascade so
  // R2-R8 don't end up with the right Type but $0 rates. Operator-
  // overridden rows (`override === true`) are honoured — their rates
  // are preserved even when type_name is overwritten in force mode.
  const cascadeFieldToSiblings = (wt, sourceKey, field, value, { force = false } = {}) => {
    const currentLines = groups[wt] || [];
    const siblings = currentLines.filter(l => l._key !== sourceKey);
    const total = siblings.length;
    // Count up-front which siblings will actually be touched so we can
    // give the operator accurate toast feedback (React 18 defers the
    // functional state updater so counting inside setGroups races).
    const willTouch = siblings.filter(l => {
      if (force) return true;
      const current = l[field];
      return !(current && String(current).trim() !== '');
    });
    const touched = willTouch.length;
    if (touched === 0) return { touched, total };

    // Look up the rate card row for type_name cascades so we can stamp
    // the full row of rate-$ + supplier fields onto each cascaded line.
    const rateCard = field === 'type_name'
      ? (rates[wt] || []).find(r => r.type_name === value)
      : null;
    setGroups(prev => {
      const next = { ...prev };
      next[wt] = (prev[wt] || []).map(l => {
        if (l._key === sourceKey) return l;
        const current = l[field];
        if (!force && current && String(current).trim() !== '') return l;
        const patch = { [field]: value };
        // Type cascade — stamp the rate card too (unless the operator
        // has explicitly overridden this row's rates).
        if (field === 'type_name' && rateCard && !l.override) {
          if (rateCard.buy_cost != null)         patch.buy_cost        = rateCard.buy_cost;
          if (rateCard.materials_rate != null)   patch.materials_rate  = rateCard.materials_rate;
          if (rateCard.labour_rate != null)      patch.labour_rate     = rateCard.labour_rate;
          if (rateCard.contractor_rate != null)  patch.contractor_rate = rateCard.contractor_rate;
          if (rateCard.supplier)                 patch.supplier        = rateCard.supplier;
          if (rateCard.supplier_code)            patch.supplier_code   = rateCard.supplier_code;
          if (rateCard.materials_desc)           patch.materials_desc  = rateCard.materials_desc;
          if (rateCard.labour_details)           patch.labour_details  = rateCard.labour_details;
          // Also stamp material/profile if the rate card has them and
          // the sibling row is blank — keeps per-row filters consistent.
          if (rateCard.material && !l.material)  patch.material        = rateCard.material;
          if (rateCard.profile && !l.profile)    patch.profile         = rateCard.profile;
        }
        return { ...l, ...patch };
      });
      return next;
    });
    setDirty(true);
    return { touched, total };
  };

  // Iter149/150 — Spouting Bracket "Push to BOM" handler.
  // Synthesizes / replaces auto-tagged Spouting component_lines for
  // brackets + droppers + stop ends + mitres + consumables based on the
  // running totals computed by SpoutingBracketSummary. Each push is
  // idempotent: rows whose notes start with [AUTO:*] are stripped first,
  // then one new row per derived line is appended.
  // Iter155 — Aquaseal/Dektite "Push Clear Silicone to BOM" — standalone
  // button on the Aquaseal section header. Same nudge math as the
  // Bracket-Summary push; uses the appraisal's silicone_buffer_pct
  // (falls back to 10%). Idempotent — replaces existing
  // [AUTO:silicone_clear] rows in the Misc section on every click.
  const pushClearSiliconeToBom = useCallback(() => {
    const cfg = gutterCtx || {};
    const siliconeBufferPct = Number(cfg.siliconeBufferPct ?? 10);
    const findRateAny = (...needles) => {
      for (const grp of Object.values(rates || {})) {
        const hit = (grp || []).find(r => {
          const tn = String(r.type_name || '').toLowerCase();
          return needles.every(n => tn.includes(n.toLowerCase()));
        });
        if (hit) return hit;
      }
      return null;
    };
    setGroups(prev => {
      const aquaLines = (prev.Aquaseal || []).filter(l => !isAutoBracketLine(l));
      if (aquaLines.length === 0) {
        toast.info('No Dektite lines to push Clear Silicone for.');
        return prev;
      }
      const aquaQty = aquaLines.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
      if (aquaQty <= 0) {
        toast.info('Dektite line quantities total zero — nothing to push.');
        return prev;
      }
      const clearRaw = aquaQty * 0.10;
      const clearCeil = Math.ceil(clearRaw);
      const clearFrac = clearRaw - Math.floor(clearRaw);
      const clearNudgeThreshold = 1 - (siliconeBufferPct || 0) / 100;
      const clearNudge = clearRaw > 0 && clearFrac > 0 && clearFrac >= clearNudgeThreshold ? 1 : 0;
      const clearTotal = clearCeil + clearNudge;
      if (clearTotal <= 0) return prev;
      const rate = findRateAny('clear', 'silicone') || findRateAny('silicone', 'clear') || (rates.Misc || []).find(r => /clear.*silicone|silicone.*clear/i.test(r.type_name || '')) || {
        type_name: 'Clear Silicone', buy_cost: 0, materials_rate: 0, labour_rate: 0,
        contractor_rate: 0, supplier: '', supplier_code: '', materials_desc: '', labour_details: '',
      };
      const nudgeNote = clearNudge > 0 ? `+1 nudge (frac ${clearFrac.toFixed(2)} ≥ ${clearNudgeThreshold.toFixed(2)})` : '';
      // Strip any existing [AUTO:silicone_clear] Misc rows and append.
      const existingMisc = prev.Misc || [];
      const keptMisc = existingMisc.filter(l => !/^\[AUTO:silicone_clear\]/i.test((l && l.notes) || ''));
      const newRow = {
        _key: `auto_silicone_clear_${Date.now()}`, component_type: 'Misc',
        type_name: rate.type_name,
        profile: rate.profile || '',
        quantity: clearTotal, length: 0,
        buy_cost: Number(rate.buy_cost) || 0,
        materials_rate: Number(rate.materials_rate) || 0,
        labour_rate: Number(rate.labour_rate) || 0,
        contractor_rate: Number(rate.contractor_rate) || 0,
        supplier: rate.supplier || '', supplier_code: rate.supplier_code || '',
        materials_desc: rate.materials_desc || '', labour_details: rate.labour_details || '',
        notes: [`[AUTO:silicone_clear]`, `10%×${aquaQty} dektite = ${clearRaw.toFixed(2)} raw`, nudgeNote].filter(Boolean).join(' · '),
        override: false, mitre_count: 0, stop_end_left: false, stop_end_right: false, outlet_size: '', outlet_qty: 0,
      };
      return { ...prev, Misc: [...keptMisc, newRow] };
    });
    setDirty(true);
    toast.success(`Pushed Clear Silicone to BOM (Misc section). Click Save to persist.`);
  }, [rates, gutterCtx]);


  // Iter260b — Aquaseal/Dektite "Extra Aluminium Clips" upsert.
  // Operator types a quantity below the section header; we upsert a
  // single [AUTO:extra_clips] Aquaseal line bound to the accessory
  // catalogue row (is_accessory=true, falls back to name~/clip/i).
  // qty=0 removes the line. Idempotent on each call so the operator
  // can re-adjust without piling up duplicates.
  const upsertExtraClipsLine = useCallback((rawQty) => {
    const qty = Math.max(0, Math.floor(Number(rawQty) || 0));
    setGroups(prev => {
      const aqua = prev.Aquaseal || [];
      // Strip any prior auto-extra-clips row before deciding what to do.
      const kept = aqua.filter(l => !/^\[AUTO:extra_clips\]/i.test((l && l.notes) || ''));
      if (qty <= 0) {
        if (kept.length === aqua.length) return prev; // nothing changed
        return { ...prev, Aquaseal: kept };
      }
      // Find the clip catalogue row. Preference order:
      //   1. is_accessory === true (operator-flagged in audit page).
      //   2. type_name contains "clip" but not "dektite" (so we never
      //      pick a Dektite-with-clips row by accident).
      //   3. type_name contains "clip" (broadest fallback).
      const cards = rates.Aquaseal || [];
      const rate = cards.find(r => r.is_accessory === true)
                || cards.find(r => /\bclip\b/i.test(r.type_name || '') && !/dektite/i.test(r.type_name || ''))
                || cards.find(r => /clip/i.test(r.type_name || ''));
      if (!rate) {
        toast.error('No "Aluminium Clip" catalogue row found in Aquaseal. Add one via Component Rates first.');
        return prev;
      }
      const newRow = {
        _key: `auto_extra_clips_${Date.now()}`,
        component_type: 'Aquaseal',
        type_name: rate.type_name,
        profile: rate.profile || '',
        quantity: qty,
        length: 0,
        buy_cost: Number(rate.buy_cost) || 0,
        materials_rate: Number(rate.materials_rate) || 0,
        labour_rate: Number(rate.labour_rate) || 0,
        contractor_rate: Number(rate.contractor_rate) || 0,
        supplier: rate.supplier || '',
        supplier_code: rate.supplier_code || '',
        materials_desc: rate.materials_desc || '',
        labour_details: rate.labour_details || '',
        notes: `[AUTO:extra_clips] Stand-alone clips request × ${qty}`,
        override: false,
        mitre_count: 0, stop_end_left: false, stop_end_right: false,
        outlet_size: '', outlet_qty: 0,
        folds: [], start_angle_deg: 0,
        material: rate.material || '',
      };
      return { ...prev, Aquaseal: [...kept, newRow] };
    });
    setDirty(true);
  }, [rates]);


  // Iter260e — Multidek Clips + Bracket Screws auto-line manager.
  // Drives two [AUTO:multidek_clips] and [AUTO:multidek_screws] lines
  // off the Multidek sheet area: clips ≈ 6 per m² × (1+buf), screws ≈
  // 3 per clip × (1+buf). Bar inputs override the derived value; the
  // last-committed value lives in the line's quantity so it persists.
  //
  // payload = { clips_qty, screws_qty, buf_pct } — any subset accepted.
  // If `clips_qty` is null → remove the clips auto-line. Same for screws.
  const upsertMultidekAuxLines = useCallback((payload) => {
    const { clips_qty, screws_qty, buf_pct } = payload || {};
    setGroups(prev => {
      const sheet = prev.Sheet || [];
      const cards = rates.Sheet || [];
      const clipRate   = cards.find(r => /^multidek clips$/i.test(r.type_name || ''))
                      || cards.find(r => /multidek.*clips/i.test(r.type_name || ''));
      const screwRate  = cards.find(r => /multidek.*bracket\s*screws/i.test(r.type_name || ''))
                      || cards.find(r => /multidek.*screws/i.test(r.type_name || ''));

      // Strip any existing multidek auto-lines, then add fresh ones.
      let next = sheet.filter(l => !/^\[AUTO:(multidek_clips|multidek_screws)\]/i.test((l && l.notes) || ''));

      const makeAuto = (rate, qty, kind) => ({
        _key: `auto_${kind}_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
        component_type: 'Sheet',
        type_name: rate.type_name,
        profile: rate.sheet_profile || 'MultiDek',
        quantity: qty,
        length: 0,
        buy_cost: Number(rate.buy_cost) || 0,
        materials_rate: Number(rate.materials_rate) || 0,
        labour_rate: Number(rate.labour_rate) || 0,
        contractor_rate: Number(rate.contractor_rate) || 0,
        supplier: rate.supplier || '',
        supplier_code: rate.supplier_code || '',
        materials_desc: rate.materials_desc || '',
        labour_details: rate.labour_details || '',
        notes: `[AUTO:${kind}] Multidek aux × ${qty} · buf=${buf_pct ?? 10}%`,
        override: false,
        mitre_count: 0, stop_end_left: false, stop_end_right: false,
        outlet_size: '', outlet_qty: 0,
        folds: [], start_angle_deg: 0,
        material: rate.material || '',
      });

      if (clips_qty != null && clips_qty > 0) {
        if (clipRate) next.push(makeAuto(clipRate, clips_qty, 'multidek_clips'));
        else toast.error('No "Multidek Clips" rate row found — add one via /catalogue-audit/sheet.');
      }
      if (screws_qty != null && screws_qty > 0) {
        if (screwRate) next.push(makeAuto(screwRate, screws_qty, 'multidek_screws'));
        else toast.error('No "Multidek Bracket Screws" rate row found — add one via /catalogue-audit/sheet.');
      }
      return { ...prev, Sheet: next };
    });
    setDirty(true);
  }, [rates]);



  // Iter260k — Foam Filler auto-line manager. Maintains one auto-line
  // per (profile, tip) pair. Qty = sum of sheet line quantities for
  // that profile. payload = { selections: { "Corrugate": "narrow"|"wide"|"both"|"none", ... }, flat: { "Flat 40x25": meters, ... } }
  const upsertFoamFillerLines = useCallback((payload) => {
    const { selections = {}, flat = {} } = payload || {};
    setGroups(prev => {
      const sheet = prev.Sheet || [];
      const sheetRates = rates.Sheet || [];
      // Strip all existing foam filler auto-lines.
      let next = sheet.filter(l => !/^\[AUTO:foam_filler/i.test((l && l.notes) || ''));
      // Helper: find the foam filler rate row for a profile + tip.
      const findRate = (profile, tip) => sheetRates.find(r => {
        const n = (r.type_name || '').toLowerCase();
        return n.startsWith('foam filler ') && n.includes(profile.toLowerCase()) &&
               (tip === 'corrugate' ? !n.includes('tip') : n.includes(`${tip} tip`));
      });
      const findFlat = (key) => sheetRates.find(r => (r.type_name || '').toLowerCase().includes(key.toLowerCase()));
      const makeLine = (rate, qty, marker) => ({
        _key: `auto_${marker}_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
        component_type: 'Sheet', type_name: rate.type_name, profile: rate.sheet_profile || '',
        quantity: qty, length: 0,
        buy_cost: Number(rate.buy_cost)||0, materials_rate: Number(rate.materials_rate)||0,
        labour_rate: 0, contractor_rate: 0,
        supplier: rate.supplier || '', supplier_code: rate.supplier_code || '',
        materials_desc: rate.materials_desc || '', labour_details: '',
        notes: `[AUTO:${marker}] qty=${qty}`,
        override: false, mitre_count: 0, stop_end_left: false, stop_end_right: false,
        outlet_size: '', outlet_qty: 0, folds: [], start_angle_deg: 0, material: 'Foam',
      });
      // Per-profile auto-lines (qty = sheet count).
      Object.entries(selections).forEach(([profile, tip]) => {
        if (!tip || tip === 'none') return;
        const sheetCount = sheet
          .filter(l => !/^\[AUTO:/i.test(l.notes||'') && (l.profile||'').toLowerCase() === profile.toLowerCase())
          .reduce((acc, l) => acc + (Number(l.quantity) || 0), 0);
        if (sheetCount <= 0) return;
        const tips = tip === 'both' ? ['narrow', 'wide'] : [tip];
        tips.forEach(t => {
          const rate = profile.toLowerCase() === 'corrugate'
            ? findRate(profile, 'corrugate')
            : findRate(profile, t);
          if (rate) next.push(makeLine(rate, sheetCount, `foam_filler_${profile.toLowerCase()}_${t}`));
        });
      });
      // Flat strips — operator-entered meters.
      Object.entries(flat).forEach(([key, meters]) => {
        const m = Number(meters) || 0;
        if (m <= 0) return;
        const rate = findFlat(key);
        if (rate) next.push(makeLine(rate, Math.ceil(m), `foam_filler_${key.replace(/[^a-z0-9]/gi,'_').toLowerCase()}`));
      });
      return { ...prev, Sheet: next };
    });
    setDirty(true);
  }, [rates]);



  // Iter260l — Edging auto-line manager (Flashing section).
  // Reads template-level `edges.{start,end}` for each non-AUTO Flashing
  // line, then aggregates total meters per edge type (Flashguard / Ali).
  // Each end fitted with the edge contributes one strip running the
  // full flashing length (qty × length). The bar may override the
  // computed total meters per type. Upserts `[AUTO:edging_flashguard]`
  // and `[AUTO:edging_ali]` Flashing lines bound to the catalogue rate
  // rows.
  //
  // payload = { flashguard_m, ali_m } — meters (Number or null).
  // null → leave the existing auto-line untouched (caller supplied no
  // value for that type). 0 → strip that auto-line.
  const upsertEdgingLines = useCallback((payload) => {
    const { flashguard_m, ali_m } = payload || {};
    setGroups(prev => {
      const flashing = prev.Flashing || [];
      const flashingRates = rates.Flashing || [];
      const findRate = (label) => flashingRates.find(r => (r.type_name || '').toLowerCase() === label.toLowerCase())
                               || flashingRates.find(r => new RegExp(`^edging\\s+${label.replace(/edging\\s+/i,'')}`, 'i').test(r.type_name || ''));
      const fgRate  = findRate('Edging Flashguard');
      const aliRate = findRate('Edging ALI');

      // Strip the two auto-lines first; we re-add if qty > 0.
      let next = flashing.filter(l => !/^\[AUTO:edging_(flashguard|ali)\]/i.test((l && l.notes) || ''));

      const makeAuto = (rate, qty, kind) => ({
        _key: `auto_${kind}_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
        component_type: 'Flashing', type_name: rate.type_name,
        flashing_type: rate.type_name, flashing_template_id: '',
        girth_mm: Number(rate.girth_mm) || 0, folds_qty: 0,
        quantity: qty, length: 1,
        buy_cost: Number(rate.buy_cost) || 0, materials_rate: Number(rate.materials_rate) || 0,
        labour_rate: 0, contractor_rate: 0,
        supplier: rate.supplier || '', supplier_code: rate.supplier_code || '',
        materials_desc: rate.materials_desc || '', labour_details: '',
        notes: `[AUTO:${kind}] qty=${qty}m`,
        override: false, mitre_count: 0, stop_end_left: false, stop_end_right: false,
        outlet_size: '', outlet_qty: 0, folds: [], start_angle_deg: 0,
        material: rate.material || '',
      });

      const addLine = (rate, meters, kind, label) => {
        const m = Number(meters) || 0;
        if (m <= 0) return;
        if (!rate) { toast.error(`No "${label}" rate row found — add one via /catalogue-audit/flashing.`); return; }
        next.push(makeAuto(rate, Math.ceil(m), kind));
      };

      // Re-add only when caller supplied a non-null number.
      if (flashguard_m != null) addLine(fgRate, flashguard_m, 'edging_flashguard', 'Edging Flashguard');
      if (ali_m       != null) addLine(aliRate, ali_m,       'edging_ali',         'Edging ALI');

      return { ...prev, Flashing: next };
    });
    setDirty(true);
  }, [rates]);




  // Iter260g + Iter276-mortar — Bedding & Pointing materials auto-line manager.
  // Shared handler for both Ridge and Barge sections. Upserts:
  //   [AUTO:bedding_mortar_{section}]       — mortar bag count
  //   [AUTO:pointing_compound_{section}]    — pointing bucket count
  //   [AUTO:plasticiser_{section}]          — plasticiser mL (stock item)
  // The mortar / pointing / plasticiser rate rows live in Misc as is_accessory=true.
  //
  // payload = {
  //   mortar_qty, pointing_qty,        // rounded ints; 0 → strip line
  //   pointing_product_name,           // which Misc row to bind for pointing
  //   mortar_supplier,                 // operator's chosen supplier override
  //   buf_pct, mortar_coverage,        // notes-tagged for reload-safety
  //   pointing_coverage, cap_mL,
  // }
  const upsertBedPointAuxLines = useCallback((section, payload) => {
    const {
      mortar_qty, pointing_qty,
      pointing_product_name,
      mortar_supplier,
      buf_pct, mortar_coverage, pointing_coverage, cap_mL,
    } = payload || {};

    setGroups(prev => {
      const sectionLines = prev[section] || [];
      const miscRates = rates.Misc || [];
      const mortarRate = miscRates.find(r => /^mortar bag 20kg$/i.test(r.type_name || ''));
      const pointingRate = miscRates.find(r => (r.type_name || '') === pointing_product_name)
                       || miscRates.find(r => r.is_accessory && /pointing|flexipoint|pointwork/i.test(r.type_name || ''));
      const plasticiserRate = miscRates.find(r => /^plasticiser$/i.test(r.type_name || ''));

      const markerMortar      = `[AUTO:bedding_mortar_${section.toLowerCase()}]`;
      const markerPointing    = `[AUTO:pointing_compound_${section.toLowerCase()}]`;
      const markerPlasticiser = `[AUTO:plasticiser_${section.toLowerCase()}]`;

      // Strip all three existing auto-lines first.
      let next = sectionLines.filter(l => {
        const notes = String(l && l.notes || '');
        return !notes.startsWith(markerMortar)
            && !notes.startsWith(markerPointing)
            && !notes.startsWith(markerPlasticiser);
      });

      const makeAuto = (rate, qty, marker, supplierOverride) => ({
        _key: `auto_${marker}_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
        component_type: section,
        type_name: rate.type_name,
        profile: '',
        quantity: qty,
        length: 0,
        buy_cost: Number(rate.buy_cost) || 0,
        materials_rate: Number(rate.materials_rate) || 0,
        labour_rate: 0,
        contractor_rate: 0,
        supplier: supplierOverride || rate.supplier || '',
        supplier_code: rate.supplier_code || '',
        materials_desc: rate.materials_desc || '',
        labour_details: '',
        notes: `${marker} qty=${qty} · buf=${buf_pct ?? 10}% · cov=${marker.includes('mortar') ? mortar_coverage : pointing_coverage}${marker.includes('mortar') ? ' m/bag' : ' m/bucket'} · cap=${cap_mL ?? 25}mL`,
        override: false,
        mitre_count: 0, stop_end_left: false, stop_end_right: false,
        outlet_size: '', outlet_qty: 0,
        folds: [], start_angle_deg: 0,
        material: '',
      });

      if (mortar_qty != null && mortar_qty > 0) {
        if (mortarRate) next.push(makeAuto(mortarRate, mortar_qty, markerMortar, mortar_supplier));
        else toast.error('Missing "Mortar Bag 20kg" catalogue row.');
      }
      if (pointing_qty != null && pointing_qty > 0) {
        if (pointingRate) next.push(makeAuto(pointingRate, pointing_qty, markerPointing));
        else toast.error('Missing pointing compound catalogue row.');
      }
      // Iter276-mortar — plasticiser is a stock item; we still push an
      // auto-line so the Materials tab shows the per-appraisal mL the
      // operator needs to take from stock. qty in mL (matches the
      // catalogue row's unit). Skipped when no mortar bags this section.
      const plasticiser_mL = (Number(mortar_qty) || 0) * 2 * (Number(cap_mL ?? 25) || 25);
      if (plasticiser_mL > 0) {
        if (plasticiserRate) {
          next.push({
            ...makeAuto(plasticiserRate, plasticiser_mL, markerPlasticiser),
            notes: `${markerPlasticiser} qty=${plasticiser_mL} mL · ${mortar_qty} bag(s) × 2 caps × ${cap_mL ?? 25} mL · stock item`,
          });
        }
        // Silent if the catalogue row is missing — plasticiser is
        // optional / advisory; we don't want a noisy toast on every save.
      }
      return { ...prev, [section]: next };
    });
    setDirty(true);
  }, [rates]);



  const pushBracketsToBom = useCallback(({ groups: bracketGroups, grand, bracketRate, finish, spanM, bufferPct, mitreExtraM, totalRivets, totalSilicone, rivetBufferPct, siliconeBufferPct, rivetBufferAdd, siliconeNudge, siliconeRaw, rivetsRaw }) => {
    if (!bracketRate || !bracketGroups || bracketGroups.length === 0) return;
    // Helper — find a rate row whose name matches given needles in
    // ANY component_type group. Silicone + rivet rates sometimes live
    // under Misc / Aquaseal / Spouting depending on supplier setup —
    // search broadly so the push always finds a real $$ row when one
    // exists. Falls back to a $0 stub so the auto-row still appears.
    const findRateAny = (...needles) => {
      for (const grp of Object.values(rates || {})) {
        const hit = (grp || []).find(r => {
          const tn = String(r.type_name || '').toLowerCase();
          return needles.every(n => tn.includes(n.toLowerCase()));
        });
        if (hit) return hit;
      }
      return null;
    };
    const findSpoutingRate = (...needles) => (rates.Spouting || []).find(r => {
      const tn = String(r.type_name || '').toLowerCase();
      return needles.every(n => tn.includes(n.toLowerCase()));
    });
    const dropperRates = {
      '80mm': findSpoutingRate('dropper', '80'),
      '65mm': findSpoutingRate('dropper', '65'),
      'rect': findSpoutingRate('dropper', 'rect') || findSpoutingRate('dropper', 'rectangle'),
    };
    const stopEndRate = findSpoutingRate('stop', 'end');
    const mitreRate   = findSpoutingRate('mitre');
    // Iter155 — rivet + silicone rates now resolve in the Misc section
    // first (matches the new routing) and fall back to any section.
    const rivetRate           = findRateAny('rivet', 'colour') || findRateAny('rivet') || (rates.Misc || []).find(r => /rivet/i.test(r.type_name || ''));
    const siliconeModifiedRate = findRateAny('modified', 'silicone') || findRateAny('silicone', 'modified') || (rates.Misc || []).find(r => /modified.*silicone|silicone.*modified/i.test(r.type_name || ''));
    const siliconeClearRate    = findRateAny('clear', 'silicone') || findRateAny('silicone', 'clear') || (rates.Misc || []).find(r => /clear.*silicone|silicone.*clear/i.test(r.type_name || ''));
    const stubRate = (label) => ({
      type_name: label, buy_cost: 0, materials_rate: 0, labour_rate: 0,
      contractor_rate: 0, supplier: '', supplier_code: '', materials_desc: '',
      labour_details: '',
    });

    setGroups(prev => {
      const existingSpouting = prev.Spouting || [];
      const existingMisc     = prev.Misc     || [];
      // Strip ALL previous auto rows from BOTH sections so re-push is
      // idempotent. Iter155: rivets + silicone may have been written
      // into Spouting by older pushes — strip them there too.
      const keptSpouting = existingSpouting.filter(l => !isAutoBracketLine(l));
      const keptMisc     = existingMisc.filter(l => !isAutoBracketLine(l));
      const spoutingRows = [];
      const miscRows     = [];
      let n = 0;

      // 1. Brackets — one row per gutter type group (stays in Spouting).
      bracketGroups.filter(g => g.bracket_qty > 0).forEach((g) => {
        const noteParts = [
          AUTO_BRACKET_TAG, g.type_name,
          `${g.ordered_m.toFixed(2)}m / ${Math.round(spanM * 1000)}mm`, `${g.bracket_raw} raw`,
        ];
        if (bufferPct > 0)  noteParts.push(`+${g.buffer_amt} (${bufferPct}% buffer)`);
        if (g.mitre_count > 0) noteParts.push(`incl ${g.mitre_count} mitre × ${Math.round(mitreExtraM * 1000)}mm`);
        noteParts.push(finish);
        spoutingRows.push({
          _key: `auto_bracket_${Date.now()}_${n++}`, component_type: 'Spouting',
          // Iter156-A — type_name must be the EXACT rate-row name so
          // the Components-tab Type dropdown can match it. Originating
          // gutter type lives in `notes`. (Earlier we used a composite
          // `${bracketRate.type_name} · ${g.type_name}` which left the
          // dropdown blank.)
          type_name: bracketRate.type_name,
          profile: bracketRate.profile || '',
          quantity: g.bracket_qty, length: 0,
          buy_cost:        Number(bracketRate.buy_cost) || 0,
          materials_rate:  Number(bracketRate.materials_rate) || 0,
          labour_rate:     Number(bracketRate.labour_rate) || 0,
          contractor_rate: Number(bracketRate.contractor_rate) || 0,
          supplier: bracketRate.supplier || '', supplier_code: bracketRate.supplier_code || '',
          materials_desc: bracketRate.materials_desc || '', labour_details: bracketRate.labour_details || '',
          notes: noteParts.join(' · '), override: false,
          mitre_count: 0, stop_end_left: false, stop_end_right: false, outlet_size: '', outlet_qty: 0,
        });
      });

      // 2. Droppers — one row per outlet size (Spouting).
      const dropperByName = {};
      bracketGroups.forEach(g => {
        Object.entries(g.outlets_by_size || {}).forEach(([size, qty]) => {
          dropperByName[size] = (dropperByName[size] || 0) + qty;
        });
      });
      Object.entries(dropperByName).forEach(([size, qty]) => {
        const rate = dropperRates[size] || stubRate(`Dropper ${size}`);
        spoutingRows.push({
          _key: `auto_dropper_${Date.now()}_${n++}`, component_type: 'Spouting',
          // Iter156-A — exact rate-row type_name so the Type dropdown
          // matches. Size context lives in notes only.
          type_name: rate.type_name,
          profile: rate.profile || '',
          quantity: qty, length: 0,
          buy_cost: Number(rate.buy_cost) || 0,
          materials_rate: Number(rate.materials_rate) || 0,
          labour_rate: Number(rate.labour_rate) || 0,
          contractor_rate: Number(rate.contractor_rate) || 0,
          supplier: rate.supplier || '', supplier_code: rate.supplier_code || '',
          materials_desc: rate.materials_desc || '', labour_details: rate.labour_details || '',
          notes: `[AUTO:dropper] ${size} outlet × ${qty}`,
          override: false, mitre_count: 0, stop_end_left: false, stop_end_right: false, outlet_size: '', outlet_qty: 0,
        });
      });

      // 3. Stop ends — L / R split (Spouting).
      const stopL = bracketGroups.reduce((s, g) => s + g.stop_l, 0);
      const stopR = bracketGroups.reduce((s, g) => s + g.stop_r, 0);
      const stopEndLRate = findSpoutingRate('stop', 'end', 'left') || findSpoutingRate('stop', 'end', ' l') || stopEndRate;
      const stopEndRRate = findSpoutingRate('stop', 'end', 'right') || findSpoutingRate('stop', 'end', ' r') || stopEndRate;
      [
        { side: 'L', qty: stopL, base: stopEndLRate },
        { side: 'R', qty: stopR, base: stopEndRRate },
      ].forEach(({ side, qty, base }) => {
        if (qty <= 0) return;
        const stub = base || stubRate(`Stop End ${side}`);
        spoutingRows.push({
          _key: `auto_stop_end_${side}_${Date.now()}_${n++}`, component_type: 'Spouting',
          // Iter156-A — exact rate-row type_name. L/R side lives in notes.
          type_name: stub.type_name,
          profile: stub.profile || '',
          quantity: qty, length: 0,
          buy_cost: Number(stub.buy_cost) || 0,
          materials_rate: Number(stub.materials_rate) || 0,
          labour_rate: Number(stub.labour_rate) || 0,
          contractor_rate: Number(stub.contractor_rate) || 0,
          supplier: stub.supplier || '', supplier_code: stub.supplier_code || '',
          materials_desc: stub.materials_desc || '', labour_details: stub.labour_details || '',
          notes: `[AUTO:stop_end] ${side}-hand × ${qty}`,
          override: false, mitre_count: 0, stop_end_left: false, stop_end_right: false, outlet_size: '', outlet_qty: 0,
        });
      });

      // 4. Mitres — single roll-up row (Spouting).
      if (grand.mitre_count > 0) {
        const rate = mitreRate || stubRate('Mitre');
        spoutingRows.push({
          _key: `auto_mitre_${Date.now()}_${n++}`, component_type: 'Spouting',
          type_name: rate.type_name,
          profile: rate.profile || '',
          quantity: grand.mitre_count, length: 0,
          buy_cost: Number(rate.buy_cost) || 0,
          materials_rate: Number(rate.materials_rate) || 0,
          labour_rate: Number(rate.labour_rate) || 0,
          contractor_rate: Number(rate.contractor_rate) || 0,
          supplier: rate.supplier || '', supplier_code: rate.supplier_code || '',
          materials_desc: rate.materials_desc || '', labour_details: rate.labour_details || '',
          notes: `[AUTO:mitre] count ${grand.mitre_count} × ${Math.round(mitreExtraM * 1000)}mm extra length already in brackets`,
          override: false, mitre_count: 0, stop_end_left: false, stop_end_right: false, outlet_size: '', outlet_qty: 0,
        });
      }

      // 5. Rivets — now routed to MISC. Colour-matched to the roof's
      // iron colour so the supplier order includes the right pop-rivet
      // colour. Buffer applied: ceil(raw × (1 + rivet_buffer_pct/100)).
      if (false && totalRivets > 0) {
        const rate = rivetRate || stubRate('Pop Rivets · 4mm');
        const colourLabel = roofColour ? ` · ${roofColour}` : '';
        const bufferNote  = rivetBufferAdd > 0 ? `+${rivetBufferAdd} (${rivetBufferPct}% buffer)` : '';
        const rawNote     = `${Math.ceil(rivetsRaw)} raw`;
        const breakdown   = `4×${grand.outlet_qty} dropper + 4×${grand.stop_ends} stop-end + 2.5×${grand.mitre_count} mitre`;
        miscRows.push({
          _key: `auto_rivet_${Date.now()}_${n++}`, component_type: 'Misc',
          // Iter156-A — exact rate-row type_name so the Misc Type
          // dropdown can render the picked rate. Colour is kept on a
          // dedicated `colour` field + echoed in the notes.
          type_name: rate.type_name,
          profile: rate.profile || '',
          quantity: totalRivets, length: 0,
          buy_cost: Number(rate.buy_cost) || 0,
          materials_rate: Number(rate.materials_rate) || 0,
          labour_rate: Number(rate.labour_rate) || 0,
          contractor_rate: Number(rate.contractor_rate) || 0,
          supplier: rate.supplier || '', supplier_code: rate.supplier_code || '',
          materials_desc: rate.materials_desc || '', labour_details: rate.labour_details || '',
          colour: roofColour || '',
          notes: [`[AUTO:rivet]`, breakdown, `= ${rawNote}`, bufferNote, colourLabel.trim().replace(/^· /, '')].filter(Boolean).join(' · '),
          override: false, mitre_count: 0, stop_end_left: false, stop_end_right: false, outlet_size: '', outlet_qty: 0,
        });
      }

      // 6. Modified Silicone — routed to MISC. Used for mitres / stop
      // ends / droppers per operator's spec. Nudged-up tube count if
      // raw fractional is within the buffer threshold of a full tube.
      if (false && totalSilicone > 0) {
        const rate = siliconeModifiedRate || stubRate('Modified Silicone');
        const nudgeNote = siliconeNudge > 0 ? `+1 nudge (frac ${(siliconeRaw - Math.floor(siliconeRaw)).toFixed(2)} ≥ ${(1 - siliconeBufferPct / 100).toFixed(2)})` : '';
        miscRows.push({
          _key: `auto_silicone_mod_${Date.now()}_${n++}`, component_type: 'Misc',
          type_name: rate.type_name,
          profile: rate.profile || '',
          quantity: totalSilicone, length: 0,
          buy_cost: Number(rate.buy_cost) || 0,
          materials_rate: Number(rate.materials_rate) || 0,
          labour_rate: Number(rate.labour_rate) || 0,
          contractor_rate: Number(rate.contractor_rate) || 0,
          supplier: rate.supplier || '', supplier_code: rate.supplier_code || '',
          materials_desc: rate.materials_desc || '', labour_details: rate.labour_details || '',
          notes: [`[AUTO:silicone_modified]`, `10%×${grand.outlet_qty} dropper + 15%×${grand.stop_ends} stop-end + 7.5%×${grand.mitre_count} mitre = ${siliconeRaw.toFixed(2)} raw`, nudgeNote].filter(Boolean).join(' · '),
          override: false, mitre_count: 0, stop_end_left: false, stop_end_right: false, outlet_size: '', outlet_qty: 0,
        });
      }

      // 7. Clear Silicone — routed to MISC for the Aquaseal/Dektite
      // section. Raw count = sum of all Aquaseal line quantities × a
      // 10% per-Dektite tube assumption (matches the standard install
      // kit: 1 tube per ~10 dektites). Buffer math identical to
      // Modified Silicone. Only emitted when the Aquaseal section has
      // lines.
      const aquaLines = (prev.Aquaseal || []).filter(l => !isAutoBracketLine(l));
      if (aquaLines.length > 0) {
        const aquaQty = aquaLines.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
        if (aquaQty > 0) {
          // 1 tube per 10 dektites (10% per dektite — same rule as
          // mitres → silicone).
          const clearRaw = aquaQty * 0.10;
          const clearCeil = Math.ceil(clearRaw);
          const clearFrac = clearRaw - Math.floor(clearRaw);
          const clearNudgeThreshold = 1 - (siliconeBufferPct || 0) / 100;
          const clearNudge = clearRaw > 0 && clearFrac > 0 && clearFrac >= clearNudgeThreshold ? 1 : 0;
          const clearTotal = clearCeil + clearNudge;
          if (false && clearTotal > 0) {
            const rate = siliconeClearRate || stubRate('Clear Silicone');
            const nudgeNote = clearNudge > 0 ? `+1 nudge (frac ${clearFrac.toFixed(2)} ≥ ${clearNudgeThreshold.toFixed(2)})` : '';
            miscRows.push({
              _key: `auto_silicone_clear_${Date.now()}_${n++}`, component_type: 'Misc',
              type_name: rate.type_name,
              profile: rate.profile || '',
              quantity: clearTotal, length: 0,
              buy_cost: Number(rate.buy_cost) || 0,
              materials_rate: Number(rate.materials_rate) || 0,
              labour_rate: Number(rate.labour_rate) || 0,
              contractor_rate: Number(rate.contractor_rate) || 0,
              supplier: rate.supplier || '', supplier_code: rate.supplier_code || '',
              materials_desc: rate.materials_desc || '', labour_details: rate.labour_details || '',
              notes: [`[AUTO:silicone_clear]`, `10%×${aquaQty} dektite = ${clearRaw.toFixed(2)} raw`, nudgeNote].filter(Boolean).join(' · '),
              override: false, mitre_count: 0, stop_end_left: false, stop_end_right: false, outlet_size: '', outlet_qty: 0,
            });
          }
        }
      }

      return { ...prev, Spouting: [...keptSpouting, ...spoutingRows], Misc: [...keptMisc, ...miscRows] };
    });
    setDirty(true);
    const stopL = bracketGroups.reduce((s, g) => s + g.stop_l, 0);
    const stopR = bracketGroups.reduce((s, g) => s + g.stop_r, 0);
    const sizes = ['80mm', '65mm', 'rect'].map(sz => {
      const q = bracketGroups.reduce((s, g) => s + (g.outlets_by_size?.[sz] || 0), 0);
      return q > 0 ? `${q} × ${sz === 'rect' ? 'Rect' : sz}` : null;
    }).filter(Boolean).join(' + ');
    const partsMsg = [
      `${bracketGroups.reduce((s, g) => s + g.bracket_qty, 0)} brackets`,
      sizes ? `droppers (${sizes})` : null,
      stopL > 0 ? `${stopL} stop-L` : null,
      stopR > 0 ? `${stopR} stop-R` : null,
      grand.mitre_count > 0 ? `${grand.mitre_count} mitres` : null,
      totalRivets > 0 ? `${totalRivets} rivets → Misc${roofColour ? ' · ' + roofColour : ''}` : null,
      totalSilicone > 0 ? `${totalSilicone} mod. silicone → Misc` : null,
    ].filter(Boolean).join(' · ');
    toast.success(`Pushed ${partsMsg} into BOM. Click Save to persist.`);
  }, [rates, roofColour]);

  // Jun 2026 — section Material chip follows its rows: one shared row material → highlight it; mixed → "All".
  // Keyed on the row-material signature only, so a manual chip pick survives qty / length edits.
  const rowMaterialSig = MATERIAL_SECTIONS.map(sect =>
    `${sect}:${[...new Set((groups[sect] || []).map(l => l.material).filter(Boolean))].sort().join('|')}`).join(';');
  useEffect(() => {
    const patch = {};
    MATERIAL_SECTIONS.forEach(sect => {
      const mats = [...new Set((groups[sect] || []).map(l => l.material).filter(Boolean))];
      const want = mats.length === 1 ? mats[0] : (mats.length > 1 ? 'all' : null);
      if (want && (materialBySection[sect] || 'all') !== want) patch[sect] = want;
    });
    if (Object.keys(patch).length) setMaterialBySection(prev => ({ ...prev, ...patch }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowMaterialSig]);

  // Iter132 — Auto-fill on load. Runs once after the initial component
  // lines + rate cards have hydrated. Walks every Ridge / Barge / Sheet
  // / Valley / Flashing row that arrived with blank Material / Type /
  // Profile, and fills from the inferred defaults. Never overwrites
  // anything the operator has touched.
  const [autoFilledOnce, setAutoFilledOnce] = useState(false);
  useEffect(() => {
    if (autoFilledOnce) return;
    if (loading) return;
    // Need at least one rate card loaded to infer Type from rate rows.
    if (!Object.keys(rates).length) return;
    // Wait for the Roof Type fetch (or confirm it has fired) before we
    // try to infer — Type inference needs roofSubtype/Brand context.
    let anyChange = false;
    setGroups(prev => {
      const next = {};
      for (const [wt, lines] of Object.entries(prev)) {
        if (!MATERIAL_SECTIONS.includes(wt) || !lines?.length) {
          next[wt] = lines;
          continue;
        }
        const wtRates = rates[wt] || [];
        next[wt] = lines.map(l => {
          // Only fill diagram-pushed or fresh rows. We treat a row as
          // "cascadable" when EITHER `material` is blank OR the row was
          // imported from the diagram and its `type_name` is the
          // pin-ref stub ("Ridge R1 — 4.8 m") rather than a real
          // rate-card row name. Manual rows (no `import_source`) keep
          // their immutable behaviour — once the operator types a
          // value, the cascade never overwrites it.
          const isDiagramPush = l.import_source === 'roof_diagram';
          const typeMatchesRateRow = !!l.type_name && wtRates.some(r => r.type_name === l.type_name);
          const typeIsCascadable = !l.type_name || (isDiagramPush && !typeMatchesRateRow);
          // Iter141 — backfill stale rates. If the line already has a
          // type_name that resolves to a rate row but the rate-$ fields
          // are still 0 (e.g. saved before the iter140 admin-overlay
          // landed), top them up from the rate card now. Operator
          // overrides (`l.override === true`) are honoured.
          if (typeMatchesRateRow && !l.override) {
            const card = wtRates.find(r => r.type_name === l.type_name);
            const rateBlank = !(l.buy_cost > 0 || l.materials_rate > 0 || l.labour_rate > 0 || l.contractor_rate > 0);
            if (card && rateBlank) {
              const patch = {};
              if (!l.buy_cost && card.buy_cost)               patch.buy_cost        = card.buy_cost;
              if (!l.materials_rate && card.materials_rate)   patch.materials_rate  = card.materials_rate;
              if (!l.labour_rate && card.labour_rate)         patch.labour_rate     = card.labour_rate;
              if (!l.contractor_rate && card.contractor_rate) patch.contractor_rate = card.contractor_rate;
              if (!l.supplier && card.supplier)               patch.supplier        = card.supplier;
              if (!l.supplier_code && card.supplier_code)     patch.supplier_code   = card.supplier_code;
              if (!l.materials_desc && card.materials_desc)   patch.materials_desc  = card.materials_desc;
              if (!l.labour_details && card.labour_details)   patch.labour_details  = card.labour_details;
              // Also stamp material if currently blank so the per-row
              // material filter narrows correctly on the next render.
              if (!l.material) {
                // C3c-β · engine is single source of truth for material inference
                const inferred = engineMaterial;
                if (inferred && wtRates.some(r => rateHasMaterial(wt, r, inferred))) {
                  patch.material = inferred;
                }
              }
              if (Object.keys(patch).length) {
                anyChange = true;
                return { ...l, ...patch };
              }
            }
          }
          if (l.material && typeMatchesRateRow) return l;
          const patch = {};
          let material = l.material;
          if (!material) {
            // C3c-β · engine is single source of truth for material inference
            const inferred = engineMaterial;
            // Only adopt the inferred material if the section actually
            // has rate rows for it. Without this guard the cascade can
            // stamp "Bed & Point" on a Valley row and immediately
            // narrow the Type dropdown to zero options — leaving the
            // line stuck on "— Select —".
            if (inferred && wtRates.some(r => rateHasMaterial(wt, r, inferred))) {
              material = inferred;
              patch.material = inferred;
            }
          }
          if (typeIsCascadable && wtRates.length) {
            // Iter137 — pick a cascade-linked rate row, then narrow by
            // material so we never auto-pick a row that the per-row
            // material filter would hide (e.g. "Point Old" on a
            // "Bed & Point" row → dropdown can't render it). When a
            // single row survives both filters we auto-select it; when
            // multiple options remain (e.g. the 4 Replace Valley
            // metals on Old Concrete) we leave the Type blank so the
            // operator chooses from the already-filtered dropdown.
            let cascadeRows = wtRates.filter(r => {
              const rt = r.roof_type_links, bb = r.brand_links, pp = r.profile_links;
              const okRt = !Array.isArray(rt) || rt.length === 0 || (roofType    && rt.includes(roofType));
              const okBb = !Array.isArray(bb) || bb.length === 0 || (roofBrand   && bb.includes(roofBrand));
              const okPp = !Array.isArray(pp) || pp.length === 0 || (roofProfile && pp.includes(roofProfile));
              const someLinks = (Array.isArray(rt) && rt.length) || (Array.isArray(bb) && bb.length) || (Array.isArray(pp) && pp.length);
              return someLinks && okRt && okBb && okPp;
            });
            if (material) {
              cascadeRows = cascadeRows.filter(r => rateHasMaterial(wt, r, material));
            }
            if (cascadeRows.length === 1) {
              // Iter141 — also stamp the rate-$ + descriptive fields
              // from the cascade-picked rate row, mirroring what
              // `onPickType` does on a manual click. Without this the
              // line lands with the right type_name but `buy_cost /
              // materials_rate / labour_rate / contractor_rate` all
              // stuck at 0 — the operator's screenshot showed R1-R8
              // Bed & Point Modern with $0 Buy/Materials/Labour/
              // Contractor even though the cascade picked the row.
              const card = cascadeRows[0];
              patch.type_name        = card.type_name;
              if (!l.buy_cost)        patch.buy_cost        = card.buy_cost || 0;
              if (!l.materials_rate)  patch.materials_rate  = card.materials_rate || 0;
              if (!l.labour_rate)     patch.labour_rate     = card.labour_rate || 0;
              if (!l.contractor_rate) patch.contractor_rate = card.contractor_rate || 0;
              if (!l.supplier && card.supplier)              patch.supplier        = card.supplier;
              if (!l.supplier_code && card.supplier_code)    patch.supplier_code   = card.supplier_code;
              if (!l.materials_desc && card.materials_desc)  patch.materials_desc  = card.materials_desc;
              if (!l.labour_details && card.labour_details)  patch.labour_details  = card.labour_details;
            } else if (cascadeRows.length === 0 && material && wt !== 'Flashing') {
              // Flashing Type is girth/fold-driven (useFlashingRateCascade) — never name-guessed.
              // No cascade-linked row matched after the material narrow
              // — fall back to the name-based scorer (legacy behaviour
              // for sections without cascade links set yet).
              const tn = inferTypeName(wtRates, roofType, material, roofBrand);
              if (tn) {
                patch.type_name = tn;
                const card = wtRates.find(r => r.type_name === tn);
                if (card) {
                  if (!l.buy_cost)        patch.buy_cost        = card.buy_cost || 0;
                  if (!l.materials_rate)  patch.materials_rate  = card.materials_rate || 0;
                  if (!l.labour_rate)     patch.labour_rate     = card.labour_rate || 0;
                  if (!l.contractor_rate) patch.contractor_rate = card.contractor_rate || 0;
                  if (!l.supplier && card.supplier)              patch.supplier       = card.supplier;
                  if (!l.supplier_code && card.supplier_code)    patch.supplier_code  = card.supplier_code;
                  if (!l.materials_desc && card.materials_desc)  patch.materials_desc = card.materials_desc;
                  if (!l.labour_details && card.labour_details)  patch.labour_details = card.labour_details;
                }
              }
            }
            // cascadeRows.length > 1 → operator picks from the
            // already-narrowed dropdown.
          }
          if (!l.profile && roofProfile) {
            // Only apply when the section uses the same profile family
            // as the roof (Ridge/Barge/Sheet/Tile).
            patch.profile = roofProfile;
          }
          if (Object.keys(patch).length) { anyChange = true; return { ...l, ...patch }; }
          return l;
        });
      }
      return anyChange ? next : prev;
    });
    // Mark as run so we don't keep firing while the user is editing.
    setAutoFilledOnce(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, rates, quoteType, roofType, roofBrand, roofProfile, engineMaterial]);


  // Spouting gutter-length rows follow the Gutter Details banner: profile + material
  // pick the catalogue gutter row (same rows the Gutter Builder prices from). A banner
  // change re-applies to every run; otherwise only rows whose Type doesn't fit their
  // profile are re-picked. Also enforces the ends rule (mitres = 2 − stop ends).
  const prevGutterSigRef = useRef(null);
  const spoutLines = groups.Spouting;
  useEffect(() => {
    if (loading || !gutterCtx || !spoutLines?.length) return;
    const spoutRates = rates.Spouting || [];
    const sig = `${gutterCtx.gutterType}|${gutterCtx.profileLabel}|${gutterCtx.materialLabel}`;
    const ctxChanged = prevGutterSigRef.current !== null && prevGutterSigRef.current !== sig;
    if (!spoutRates.length && !ctxChanged) return;
    prevGutterSigRef.current = sig;
    const validProfiles = gutterCtx.profileLabels || [];
    let touched = false;
    const next = spoutLines.map(l => {
      if (!isGutterLengthLine(l)) return l;
      if (!l.pin_id && !/^gutter\s|spouting/i.test(l.type_name || '')) return l;  // manual non-gutter rows left alone
      let out = l;
      const mc = 2 - (l.stop_end_left ? 1 : 0) - (l.stop_end_right ? 1 : 0);
      if (Number(l.mitre_count || 0) !== mc) out = { ...out, mitre_count: mc };
      if (!l.override) {
        const prof = ctxChanged || !validProfiles.includes(l.profile) ? gutterCtx.profileLabel : l.profile;
        const fits = spoutingRowsFor(spoutRates, { ...gutterCtx, profileLabel: prof, materialLabel: '' }, true)
          .some(r => r.type_name === l.type_name);
        if (ctxChanged || !fits) {
          const rows = spoutingRowsFor(spoutRates, { ...gutterCtx, profileLabel: prof }, true);
          const patch = {};
          if (prof && out.profile !== prof) patch.profile = prof;
          if (gutterCtx.materialLabel && gutterCtx.gutterType !== 'marley' && out.material !== gutterCtx.materialLabel) patch.material = gutterCtx.materialLabel;
          if (rows.length === 1 && rows[0].type_name !== l.type_name) Object.assign(patch, spoutRatePatch(rows[0]));
          if (Object.keys(patch).length) out = { ...out, ...patch };
        }
      }
      if (out !== l) touched = true;
      return out;
    });
    if (!touched) return;
    setGroups(prev => ({ ...prev, Spouting: next }));
    setDirty(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, rates, gutterCtx, spoutLines]);


  // Iter146 — Iron-family auto-fill: when Roof Details has a Material
  // Type set (Galv / Maxx / Zcore / Zinc / etc.) and the roof type is
  // Iron, pre-select the matching rate row for every diagram-pushed
  // Sheet / Ridge / Valley / Barge / Flashing line. Mirrors the iter144
  // Spouting cascade: substring match against the rate-card type_name
  // with a material synonym map.
  useEffect(() => {
    if (loading) return;
    if (!materialType) return;
    // Only fire on Iron roofs — tile / concrete cascade is handled by
    // the iter137 roof_type_links narrow and doesn't need this layer.
    if (String(roofType || '').trim().toLowerCase() !== 'iron') return;
    const matSyn = {
      zincalume:  ['zincalume', 'zinc'],
      zinc:       ['zinc', 'zincalume'],
      galvanised: ['galv'],
      galv:       ['galv'],
      magnaflow:  ['magnaflow'],
      maxam:      ['maxam', 'maxx'],
      maxx:       ['maxam', 'maxx'],
      zinacore:   ['zinacore', 'zcore'],
      zcore:      ['zcore', 'zinacore'],
      aluguard:   ['aluguard'],
      alu:        ['alu '],
      copper:     ['copper'],
    };
    const matKeys = matSyn[String(materialType).toLowerCase()] || [String(materialType).toLowerCase()];
    setGroups(prev => {
      let touched = false;
      const next = { ...prev };
      ['Sheet', 'Ridge', 'Valley', 'Barge', 'Flashing'].forEach(wtKey => {
        const wtRates = rates[wtKey] || [];
        const lines = prev[wtKey] || [];
        if (!wtRates.length || !lines.length) return;
        const narrowed = wtRates.filter(r => {
          const tn = String(r.type_name || '').toLowerCase();
          return matKeys.some(k => k && tn.includes(k));
        });
        if (!narrowed.length) return;
        // Prefer the row whose name most closely matches the section
        // semantic (e.g. for Sheet: rows that contain the section
        // profile keyword; for Ridge: rows containing 'ridge'). Drop
        // back to the first narrowed row if no semantic match exists.
        let winner = null;
        const sectionKeyword = wtKey.toLowerCase();
        const sectionMatches = narrowed.filter(r => String(r.type_name || '').toLowerCase().includes(sectionKeyword));
        if (sectionMatches.length === 1) {
          winner = sectionMatches[0];
        } else if (sectionMatches.length > 1) {
          // Multi-match → leave operator to pick from the narrowed list,
          // but still stamp material so the dropdown filter narrows.
          winner = null;
        } else if (narrowed.length === 1) {
          winner = narrowed[0];
        }
        const patchedLines = lines.map(l => {
          if (l.override) return l;
          const typeMatchesRateRow = !!l.type_name && wtRates.some(r => r.type_name === l.type_name);
          const isDiagramPush = l.import_source === 'roof_diagram';
          const cascadable = !l.type_name || (isDiagramPush && !typeMatchesRateRow);
          // Always stamp material so the per-row Type dropdown narrows
          // to Iron rows matching the picked material — even when we
          // can't auto-pick a single winner.
          const patch = {};
          if (!l.material) patch.material = materialType;
          // Iter227 — Per-line winner refinement for Ridge: when the line
          // carries a cap_type from the Annotator (RoundTop Std/Wide),
          // narrow `sectionMatches` further by width so 'Wide' picks
          // "Wide Ridge {material} (SE)" and 'Std' picks "Ridge
          // {material} (SE)". This unblocks the auto-pick on the Ridge
          // section, which previously stayed null due to 2 width
          // variants per material.
          let lineWinner = winner;
          if (wtKey === 'Ridge' && !lineWinner && sectionMatches.length > 1) {
            const cap = String(l.cap_type || '').toLowerCase();
            if (cap) {
              const wantsWide = cap.includes('wide');
              const widthMatched = sectionMatches.filter(r => {
                const tn = String(r.type_name || '').toLowerCase();
                const isWide = tn.startsWith('wide ');
                return wantsWide ? isWide : !isWide;
              });
              if (widthMatched.length === 1) lineWinner = widthMatched[0];
            }
          }
          if (cascadable && lineWinner) {
            patch.type_name = lineWinner.type_name;
            if (!l.buy_cost && lineWinner.buy_cost)               patch.buy_cost        = lineWinner.buy_cost;
            if (!l.materials_rate && lineWinner.materials_rate)   patch.materials_rate  = lineWinner.materials_rate;
            if (!l.labour_rate && lineWinner.labour_rate)         patch.labour_rate     = lineWinner.labour_rate;
            if (!l.contractor_rate && lineWinner.contractor_rate) patch.contractor_rate = lineWinner.contractor_rate;
            if (!l.supplier && lineWinner.supplier)               patch.supplier        = lineWinner.supplier;
            if (!l.supplier_code && lineWinner.supplier_code)     patch.supplier_code   = lineWinner.supplier_code;
            if (!l.materials_desc && lineWinner.materials_desc)   patch.materials_desc  = lineWinner.materials_desc;
            if (!l.labour_details && lineWinner.labour_details)   patch.labour_details  = lineWinner.labour_details;
          }
          if (Object.keys(patch).length === 0) return l;
          touched = true;
          return { ...l, ...patch };
        });
        next[wtKey] = patchedLines;
      });
      return touched ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, rates, roofType, materialType]);


  // Phase C · C4b (2026-02c) — Fastening cascade extracted to
  // ./AppraisalComponents/hooks/useFasteningCascade.js. See that module
  // for the full doc + FST-* rule reference; state stays here in the
  // parent (props-only subform pattern).
  useFasteningCascade({
    loading,
    quoteType,
    rates,
    groupsFasteningLength: groups.Fastening?.length,
    roofM2,
    engineRatePicks,
    setGroups,
  });

  // Phase C · Row-scoped Tile Rescrew wire-in (2026-02c) — closes the
  // final anchor-contract deferral by wiring `row_position` into per-
  // row engine calls for the 3 `FST-Rescrew-Tile-*` rules. Only fires
  // on tile-rescrew quote types; no-op otherwise.
  useTileRescrewRowCascade({
    loading,
    quoteType,
    rates,
    groupsFasteningLength: groups.Fastening?.length,
    groupsFastening: groups.Fastening,
    setGroups,
  });


  // Iter153 · C3c-δ (2026-02) — Flashings template-driven rate resolution.
  // Engine `FLA-Template-Tokens` rule now owns the token pool: for a row
  // with (flashing_material, flashing_folds) we ask the engine which
  // tokens to match against the rate catalogue. The engine emits
  // `[material, ${folds}F]` today; future materials/brands can override
  // this via new rules without touching this code.
  //
  // Frontend responsibilities (stay imperative · not engine-portable):
  //   1. Filter rate rows to those matching ALL engine tokens
  //   2. Parse each candidate's `NNNW` width prefix
  //   3. Snap UP to smallest width >= template `girth_mm`
  //      (supplier stocks 50mm increments · order next-size-up + trim)
  //   4. Stamp buy/materials/labour/contractor + supplier + descriptions
  useEffect(() => {
    if (loading) return;
    const flashingRates = rates.Flashing || [];
    if (!flashingRates.length) return;
    const flashingLines = groups.Flashing || [];
    if (!flashingLines.length) return;

    // Gather distinct (material, folds) combos so we ask the engine ONCE
    // per unique tuple. Module-level cache in useCascadeEngine dedupes
    // repeat calls across renders.
    const combos = new Map();  // key -> {flashing_material, flashing_folds}
    flashingLines.forEach(l => {
      if (l.override) return;
      const mat = String(l.material || '').trim();
      const folds = Number(l.folds_qty) || 0;
      const girth = Number(l.girth_mm) || 0;
      if (!(mat && folds > 0 && girth > 0)) return;
      const key = `${mat}|${folds}`;
      if (!combos.has(key)) {
        combos.set(key, { flashing_material: mat, flashing_folds: folds });
      }
    });
    if (combos.size === 0) return;

    let cancelled = false;
    (async () => {
      // Fetch engine tokens per combo (cached · deduped by module).
      const tokensByKey = {};
      for (const [key, anchors] of combos.entries()) {
        const r = await evaluateCascade(anchors);
        const t = r?.cascade?.rate_picks?.Flashing?.tokens;
        if (Array.isArray(t) && t.length) tokensByKey[key] = t;
      }
      if (cancelled) return;

      setGroups(prev => {
        const lines = prev.Flashing || [];
        if (!lines.length) return prev;
        let touched = false;
        const next = lines.map(l => {
          if (l.override) return l;
          const girth = Number(l.girth_mm) || 0;
          const folds = Number(l.folds_qty) || 0;
          const mat = String(l.material || '').trim();
          if (!(girth > 0 && folds > 0 && mat)) return l;
          const engineTokens = tokensByKey[`${mat}|${folds}`];
          if (!engineTokens) return l;  // engine hadn't answered yet

          // Filter rate rows to those matching every engine token,
          // parse the NNNW width, snap UP to smallest width >= girth.
          const candidates = flashingRates
            .map(r => {
              const tn = String(r.type_name || '').toLowerCase();
              const allMatch = engineTokens.every(
                t => tn.includes(String(t).toLowerCase())
              );
              if (!allMatch) return null;
              const m = tn.match(/(\d+)w/);
              return m ? { row: r, width: parseInt(m[1], 10) } : null;
            })
            .filter(Boolean)
            .filter(c => c.width >= girth)
            .sort((a, b) => a.width - b.width);
          const card = candidates.length ? candidates[0].row : null;
          if (!card) return l;
          const patch = {};
          if (!flashingRates.some(r => r.type_name === l.type_name)) {
            patch.type_name = card.type_name;
          }
          if (!l.buy_cost && card.buy_cost)               patch.buy_cost        = card.buy_cost;
          if (!l.materials_rate && card.materials_rate)   patch.materials_rate  = card.materials_rate;
          if (!l.labour_rate && card.labour_rate)         patch.labour_rate     = card.labour_rate;
          if (!l.contractor_rate && card.contractor_rate) patch.contractor_rate = card.contractor_rate;
          if (!l.supplier && card.supplier)               patch.supplier        = card.supplier;
          if (!l.supplier_code && card.supplier_code)     patch.supplier_code   = card.supplier_code;
          if (!l.materials_desc && card.materials_desc)   patch.materials_desc  = card.materials_desc;
          if (!l.labour_details && card.labour_details)   patch.labour_details  = card.labour_details;
          if (Object.keys(patch).length === 0) return l;
          touched = true;
          return { ...l, ...patch };
        });
        return touched ? { ...prev, Flashing: next } : prev;
      });
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, rates, groups.Flashing]);


  // Parse a Sheet rate-card "type_name" like "Corrugate Zinc /mtr (0.55Gauge 762mm)"
  // and pull out the Profile name (first matching seeded profile) and Cover width
  // (the trailing NNNmm value, converted to metres). Returns {} when nothing matches.
  const parseSheetTypeName = (typeName) => {
    if (!typeName) return {};
    const tn = String(typeName);
    const result = {};
    // Profile — first seeded profile name that appears (case-insensitive)
    const lower = tn.toLowerCase();
    const matched = sheetProfileNames.find(p => lower.includes(p.toLowerCase()));
    if (matched) result.profile = matched;
    // Cover — find a NNN..NNNN mm token and convert to metres
    const mm = tn.match(/(\d{3,4})\s*mm/i);
    if (mm) {
      let cov = parseFloat((parseInt(mm[1], 10) / 1000).toFixed(3));
      // Snap to the nearest pre-seeded cover for that profile (so the
      // value lines up with the dropdown options on multi-cover profiles).
      const valid = matched ? (sheetProfileMap[matched] || []) : [];
      if (valid.length) {
        cov = valid.reduce((best, v) => Math.abs(v - cov) < Math.abs(best - cov) ? v : best, valid[0]);
      }
      result.cover_width = cov;
    }
    return result;
  };

  const onPickType = (wt, key, typeName) => {
    const card = (rates[wt] || []).find(r => r.type_name === typeName);
    const sheetExtras = wt === 'Sheet' ? parseSheetTypeName(typeName) : {};
    // For Flashing lines, derive and stamp the material from the chosen
    // type so the per-line dropdown stays narrowed correctly.
    //
    // Iter225 — Use `detectMaterialFor` so we never write a bogus token
    // (the previous head-split logic produced 'Flashing' for stub names
    // like 'Flashing F2', wedging downstream cascades).
    const flashingExtras = {};
    if (wt === 'Flashing' && typeName) {
      const detected = detectMaterialFor('Flashing', typeName);
      if (detected) flashingExtras.material = detected;
    }
    // Iter254 — Recoat /m² rates auto-fill the line's units (length)
    // with the appraisal's roof_m2 and set qty=1 on first pick. Operator
    // can override either field afterwards; no further cascade fires
    // when roof_m2 changes (per user spec — Iter253 cascade reserved
    // for Underlay only).
    const recoatExtras = {};
    if (wt === 'ReCoat' && typeName && /\(\s*\/?m2\s*\)/i.test(typeName)) {
      const m2 = Number(roofM2) || 0;
      if (m2 > 0) {
        recoatExtras.length = m2;
        recoatExtras.quantity = 1;
        recoatExtras.recoat_auto_filled = true;
        recoatExtras.recoat_auto_source_m2 = m2;
      }
    }
    if (!card) {
      updateLine(wt, key, { type_name: typeName, ...sheetExtras, ...flashingExtras, ...recoatExtras });
      return;
    }
    updateLine(wt, key, {
      type_name: typeName,
      buy_cost: card.buy_cost,
      materials_rate: card.materials_rate,
      labour_rate: card.labour_rate,
      contractor_rate: card.contractor_rate,
      supplier: card.supplier || '',
      supplier_code: card.supplier_code || '',
      materials_desc: card.materials_desc || '',
      labour_details: card.labour_details || '',
      override: false,
      ...sheetExtras,
      ...flashingExtras,
      ...recoatExtras,
    });
  };

  // ───────────────────────────────────────────────────────────────────
  // Iter233 — "Has Penetration?" Dektite link cascade. Three handlers
  // exposed to ComponentLineRow's DektiteLinkInline child:
  //
  //   onLinkDektite(flashingKey, payload):
  //     • Adds the entry to the Flashing row's `linked_dektites` array
  //     • Idempotently upserts a paired Aquaseal/Dektite component_line
  //       (matched by supplier_code OR dektite_id OR exact dektite_name)
  //       — increments qty when an existing line is found
  //
  //   onUnlinkDektite(flashingKey, dektite_id, { deleteAquaseal }):
  //     • Removes the entry from the Flashing row's `linked_dektites`
  //     • If `deleteAquaseal`: decrements the matched Aquaseal line's
  //       qty by 1 (or removes the line entirely when qty hits 0)
  //
  //   onChangeDektite(flashingKey, old_dektite_id, payload):
  //     • Atomic swap = unlink old + link new in one setGroups update
  // ───────────────────────────────────────────────────────────────────

  // Find the Aquaseal rate-card row that best matches a freshly-picked
  // Dektite — preference order: supplier_code, then a name substring
  // match (Ø + variant), then the first Aquaseal rate row as fallback.
  const _matchAquasealRate = (payload) => {
    const cards = rates.Aquaseal || [];
    if (!cards.length) return null;
    if (payload.supplier_code) {
      const byCode = cards.find(c => (c.supplier_code || '').trim() === payload.supplier_code);
      if (byCode) return byCode;
    }
    if (payload.dektite_name) {
      const byName = cards.find(c => (c.type_name || '').toLowerCase().includes(payload.dektite_name.toLowerCase()));
      if (byName) return byName;
    }
    // Fallback — first rate row, lets the operator see SOME pricing
    // they can refine. Better than $0 across the board.
    return null;
  };

  // _aquasealLineMatch is imported from
  // ./AppraisalComponents/helpers/baytrayLinkCascade — kept as a
  // shared helper so both onLinkDektite (upsert path) and
  // removeLine (cascade-delete path) resolve pairings identically.

  const onLinkDektite = (flashingKey, payload) => {
    setGroups(prev => {
      const next = { ...prev };
      // 1. Stamp on the Flashing row.
      next.Flashing = (prev.Flashing || []).map(l => {
        if (l._key !== flashingKey) return l;
        const list = Array.isArray(l.linked_dektites) ? [...l.linked_dektites] : [];
        list.push({ ...payload });
        return { ...l, linked_dektites: list };
      });
      // 2. Upsert the paired Aquaseal line.
      const existingAquaseal = prev.Aquaseal || [];
      const matched = _aquasealLineMatch(existingAquaseal, payload);
      if (matched) {
        // Increment qty in place.
        next.Aquaseal = existingAquaseal.map(l =>
          l._key === matched._key
            ? { ...l, quantity: (Number(l.quantity) || 0) + 1 }
            : l
        );
      } else {
        // Insert a fresh line with rate-card pre-fill when we can match one.
        const rateCard = _matchAquasealRate(payload);
        next.Aquaseal = [
          ...existingAquaseal,
          {
            _key: crypto.randomUUID(),
            ...BLANK_LINE('Aquaseal'),
            type_name: payload.dektite_name || `Ø${payload.dia_mm} Dektite`,
            supplier_code: payload.supplier_code || (rateCard?.supplier_code || ''),
            supplier: rateCard?.supplier || '',
            materials_desc: rateCard?.materials_desc || '',
            labour_details: rateCard?.labour_details || '',
            buy_cost: rateCard?.buy_cost || 0,
            materials_rate: rateCard?.materials_rate || 0,
            labour_rate: rateCard?.labour_rate || 0,
            contractor_rate: rateCard?.contractor_rate || 0,
            quantity: 1,
            length: 0,
            notes: `Linked from Flashing — auto-added by Add Penetration`,
          },
        ];
      }
      return next;
    });
    setDirty(true);
  };

  const onUnlinkDektite = (flashingKey, uid, { deleteAquaseal = false, dektite_id: dIdFallback } = {}) => {
    setGroups(prev => {
      const next = { ...prev };
      // 1. Read the removed entry's payload BEFORE we mutate the line.
      //    Match by uid (preferred — stable per-link id) and fall back
      //    to dektite_id (legacy rows without a uid yet).
      const srcLine = (prev.Flashing || []).find(l => l._key === flashingKey);
      const linkedList = Array.isArray(srcLine?.linked_dektites) ? srcLine.linked_dektites : [];
      let removedIdx = linkedList.findIndex(d => d.uid && d.uid === uid);
      if (removedIdx < 0) {
        removedIdx = linkedList.findIndex(d => !d.uid && d.dektite_id === (dIdFallback || uid));
      }
      const removed = removedIdx >= 0 ? linkedList[removedIdx] : null;
      // 2. Remove ONE entry from Flashing row (by index, not by id-filter).
      next.Flashing = (prev.Flashing || []).map(l => {
        if (l._key !== flashingKey) return l;
        const list = Array.isArray(l.linked_dektites) ? [...l.linked_dektites] : [];
        if (removedIdx >= 0) list.splice(removedIdx, 1);
        return { ...l, linked_dektites: list };
      });
      // 3. Optionally decrement / remove the Aquaseal line.
      if (deleteAquaseal && removed) {
        const matched = _aquasealLineMatch(prev.Aquaseal || [], removed);
        if (matched) {
          const remainingQty = (Number(matched.quantity) || 0) - 1;
          if (remainingQty <= 0) {
            next.Aquaseal = (prev.Aquaseal || []).filter(l => l._key !== matched._key);
          } else {
            next.Aquaseal = (prev.Aquaseal || []).map(l =>
              l._key === matched._key ? { ...l, quantity: remainingQty } : l
            );
          }
        }
      }
      return next;
    });
    setDirty(true);
  };

  const onChangeDektite = (flashingKey, oldUid, newPayload) => {
    // Unlink old (with Aquaseal decrement) then link new (with upsert),
    // collapsed into a single setGroups for an atomic swap.
    onUnlinkDektite(flashingKey, oldUid, { deleteAquaseal: true });
    // Defer the link so the unlink state lands first. React batches
    // these together in 18+, so a microtask is enough.
    Promise.resolve().then(() => onLinkDektite(flashingKey, newPayload));
  };

  // ───────────────────────────────────────────────────────────────────
  // Iter234 — "Has Backtray?" cascade on Aquaseal/Dektite rows. Mirrors
  // Iter233 from the Dektite side and closes the loop with bidirectional
  // sync — picking an existing Flashing row also stamps a reciprocal
  // `linked_dektites` entry on that Flashing row, so chips show on
  // BOTH sides of the relationship.
  //
  //   onLinkBacktray(aquasealKey, payload)
  //   onUnlinkBacktray(aquasealKey, { deleteFlashing })
  //   onChangeBacktray(aquasealKey, newPayload)
  //
  // payload shape (from BacktrayLinkInline):
  //   { uid, line_key, template_id, template_name, type_name,
  //     width_mm, length_m, source: 'picked' | 'created' }
  // ───────────────────────────────────────────────────────────────────

  // Build a reciprocal linked_dektites entry from the Aquaseal row.
  // The Flashing side uses this so the Iter233 chip shows on the
  // matched Flashing row as soon as we link from the Aquaseal side.
  const _reciprocalDektiteEntry = (aquasealLine) => ({
    uid: (crypto.randomUUID ? crypto.randomUUID() : `lk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
    dektite_id:    String(aquasealLine.supplier_code || aquasealLine.type_name || '').trim(),
    dektite_name:  String(aquasealLine.type_name || '').trim(),
    supplier_code: String(aquasealLine.supplier_code || '').trim(),
    kind:          'pipe',
    dia_mm:        Number(aquasealLine.outlet_diameter_mm) || 0,
    variant:       String(aquasealLine.dektite_variant || '').trim(),
    qty:           1,
  });

  const onLinkBacktray = (aquasealKey, payload) => {
    setGroups(prev => {
      const next = { ...prev };
      const aqLine = (prev.Aquaseal || []).find(l => l._key === aquasealKey);
      if (!aqLine) return prev;
      let finalLineKey = payload.line_key;
      let nextFlashing = [...(prev.Flashing || [])];

      if (payload.source === 'created') {
        // 1. Mint a new Flashing component_line for the Backtray.
        //    Try to match a rate-card row from the Type catalogue.
        finalLineKey = (crypto.randomUUID ? crypto.randomUUID() : `fl-${Date.now()}`);
        const newRow = {
          _key: finalLineKey,
          ...BLANK_LINE('Flashing'),
          type_name:             payload.type_name || payload.template_name || 'Backtray',
          flashing_type:         payload.template_name || '',
          flashing_template_id:  payload.template_id || '',
          girth_mm:              Number(payload.width_mm) || 0,
          quantity:              1,
          length:                Number(payload.length_m) || 0,
          notes:                 `Linked from Aquaseal — auto-added by Has Backtray?`,
          linked_dektites:       [_reciprocalDektiteEntry(aqLine)],
        };
        nextFlashing.push(newRow);
      } else if (payload.source === 'picked' && payload.line_key) {
        // 2. Existing Flashing row — stamp the reciprocal Iter233 entry.
        nextFlashing = nextFlashing.map(l => {
          if (l._key !== payload.line_key) return l;
          const list = Array.isArray(l.linked_dektites) ? [...l.linked_dektites] : [];
          list.push(_reciprocalDektiteEntry(aqLine));
          return { ...l, linked_dektites: list };
        });
      }

      // 3. Stamp linked_backtray on the Aquaseal row (with resolved line_key).
      next.Flashing = nextFlashing;
      next.Aquaseal = (prev.Aquaseal || []).map(l => l._key !== aquasealKey ? l : {
        ...l,
        linked_backtray: { ...payload, line_key: finalLineKey },
      });
      return next;
    });
    setDirty(true);
  };

  const onUnlinkBacktray = (aquasealKey, { deleteFlashing = false } = {}) => {
    setGroups(prev => {
      const next = { ...prev };
      const aqLine = (prev.Aquaseal || []).find(l => l._key === aquasealKey);
      const lb = aqLine?.linked_backtray;
      if (!lb) return prev;
      const flashingKey = lb.line_key;

      // 1. Clear the reciprocal linked_dektites entry on the Flashing row.
      //    Match by supplier_code OR dektite_name = aqLine.type_name —
      //    same matching rules the link side used.
      next.Flashing = (prev.Flashing || []).flatMap(l => {
        if (l._key !== flashingKey) return [l];
        // If asked to delete AND this was the auto-created row → drop it.
        if (deleteFlashing && lb.source === 'created') return [];
        const list = Array.isArray(l.linked_dektites) ? [...l.linked_dektites] : [];
        const matchIdx = list.findIndex(d =>
          (d.supplier_code && d.supplier_code === (aqLine.supplier_code || '')) ||
          (d.dektite_name && d.dektite_name === (aqLine.type_name || ''))
        );
        if (matchIdx >= 0) list.splice(matchIdx, 1);
        return [{ ...l, linked_dektites: list }];
      });

      // 2. Clear linked_backtray on the Aquaseal row.
      next.Aquaseal = (prev.Aquaseal || []).map(l => l._key !== aquasealKey ? l : {
        ...l,
        linked_backtray: null,
      });
      return next;
    });
    setDirty(true);
  };

  const onChangeBacktray = (aquasealKey, newPayload) => {
    onUnlinkBacktray(aquasealKey, { deleteFlashing: false });
    Promise.resolve().then(() => onLinkBacktray(aquasealKey, newPayload));
  };

  const save = async () => {
    setSaving(true);
    try {
      let lines = WORK_TYPES.flatMap(wt => (groups[wt.key] || [])
        .filter(l => l.type_name && Number(l.quantity) > 0)
        .map(({ _key, _id, ...rest }) => rest));
      if (onlySections) {
        // Embedded save is scoped: only our sections come from this form, everything else is re-read fresh.
        const { data: fresh } = await api.get(`/appraisals/${appraisalId}/component-lines`);
        const others = Object.entries(fresh.groups || {}).filter(([k]) => !onlySections.includes(k))
          .flatMap(([, arr]) => (arr || []).map(({ _id, ...rest }) => rest));
        lines = [...others, ...lines.filter(l => onlySections.includes(l.component_type))];
      }
      await api.put(`/appraisals/${appraisalId}/component-lines`, { lines });
      window.dispatchEvent(new Event('rtr:child-quote-refresh'));
      setSavedAt(new Date());
      setDirty(false);
      setUndoRefresh(k => k + 1);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      if (String(detail).startsWith('Builder lines are locked') || String(detail).startsWith('Cannot save components')) {
        setDirty(false);
        await load();
      }
      alert('Save failed: ' + detail);
    }
    setSaving(false);
  };
  // Iter190 — keep saveRef pointed at the latest save() so the
  // autosave debounce timer (defined once) can call the freshest
  // closure without re-binding on every render.
  saveRef.current = save;

  // Iter190 — autosave debounce. When dirty=true AND autosaveEnabled,
  // wait 3s of inactivity then trigger the same save() the manual
  // button uses. The countdown updates every 250 ms so the operator
  // sees "Autosaving in 2.5s…" → "Autosaving in 2.0s…" etc.
  useEffect(() => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    if (autosaveCountdownTimerRef.current) clearInterval(autosaveCountdownTimerRef.current);
    if (!dirty || !autosaveEnabled || saving || hasBlockingErrors || (onlySections && !embedTouched)) {
      setAutosaveCountdown(-1);
      return;
    }
    const totalMs = 3000;
    let remaining = totalMs;
    setAutosaveCountdown(remaining);
    autosaveCountdownTimerRef.current = setInterval(() => {
      remaining -= 250;
      if (remaining <= 0) {
        clearInterval(autosaveCountdownTimerRef.current);
        autosaveCountdownTimerRef.current = null;
        setAutosaveCountdown(-1);
        return;
      }
      setAutosaveCountdown(remaining);
    }, 250);
    autosaveTimerRef.current = setTimeout(() => {
      saveRef.current?.();
    }, totalMs);
    return () => {
      clearTimeout(autosaveTimerRef.current);
      clearInterval(autosaveCountdownTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, autosaveEnabled, saving, hasBlockingErrors, embedTouched]);

  // ── Multi-select: row checkbox toggle + bulk delete + select-all ──────
  const toggleSelected = useCallback((key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const selectAllInSection = useCallback((wt, checked) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const lines = groups[wt] || [];
      for (const l of lines) {
        if (checked) next.add(l._key); else next.delete(l._key);
      }
      return next;
    });
  }, [groups]);

  const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);

  const deleteSelected = useCallback(() => {
    if (selectedKeys.size === 0) return;
    if (!confirm(`Delete ${selectedKeys.size} selected component line${selectedKeys.size === 1 ? '' : 's'}? This is reversible via the Undo toolbar after you save.`)) return;
    setGroups((prev) => {
      const next = {};
      for (const wt of Object.keys(prev)) {
        next[wt] = (prev[wt] || []).filter((l) => !selectedKeys.has(l._key));
      }
      return next;
    });
    clearSelection();
    setDirty(true);
  }, [selectedKeys, clearSelection]);

  // ── Clear & Rebuild — wipe everything and (optionally) re-pull from
  //    the Roof Diagram or a Re-roof Builder spec. Always saves on the
  //    way out so the operator can see the new state without an extra
  //    click; the Undo toolbar lets them roll back if it wasn't what
  //    they wanted.
  const rebuildLoadSpecs = useCallback(async () => {
    try {
      const { data } = await api.get(`/appraisals/${appraisalId}/reroof-specs`);
      setReroofSpecs(Array.isArray(data) ? data : []);
    } catch (e) { setReroofSpecs([]); }
  }, [api, appraisalId]);

  const handleClearAll = useCallback(async () => {
    if (!confirm('Delete ALL component lines on this appraisal? You can undo via the Undo toolbar after the save completes.')) return;
    setRebuilding('clear');
    setRebuildMenuOpen(false);
    try {
      await api.put(`/appraisals/${appraisalId}/component-lines`, { lines: [] });
      clearSelection();
      await load();
      setSavedAt(new Date());
      setDirty(false);
      setUndoRefresh(k => k + 1);
    } catch (e) { alert('Clear failed: ' + (e?.response?.data?.detail || e.message)); }
    setRebuilding(null);
  }, [api, appraisalId, clearSelection, load]);

  // ── Push-confirmation modal state ──────────────────────────────────
  // When the operator clicks "Replace" on a push action, we surface a
  // modal that previews exactly how many existing component lines
  // would be deleted (and where they came from) before the destructive
  // call runs. "Add (keep existing)" buttons skip the modal — they
  // pass mode=merge so nothing is wiped.
  const [pushConfirm, setPushConfirm] = useState(null); // { kind, label, mode, specId?, preview }

  const runPushDiagram = useCallback(async (mode) => {
    setRebuilding('diagram');
    setRebuildMenuOpen(false);
    setPushConfirm(null);
    try {
      const { data } = await api.post(
        `/appraisals/${appraisalId}/roof-diagram-annotations/push-to-components?mode=${mode}`
      );
      clearSelection();
      await load();
      setDirty(false);
      setUndoRefresh(k => k + 1);
      if (data?.status === 'noop') {
        alert('No pin annotations on the roof diagram yet — nothing to push. Drop pins on the diagram first.');
      } else if (data?.lines_created != null) {
        // Help the operator notice the refresh — quick toast-style log.
        toast.success(`Pushed ${data.lines_created} component line${data.lines_created === 1 ? '' : 's'}.`);
      }
    } catch (e) { alert('Push from diagram failed: ' + (e?.response?.data?.detail || e.message)); }
    setRebuilding(null);
  }, [api, appraisalId, clearSelection, load]);

  const runPushSpec = useCallback(async (specId, mode) => {
    setRebuilding(specId);
    setRebuildMenuOpen(false);
    setPushConfirm(null);
    try {
      await api.post(
        `/appraisals/${appraisalId}/reroof-specs/${specId}/push-to-components?mode=${mode}`
      );
      clearSelection();
      await load();
      setUndoRefresh(k => k + 1);
    } catch (e) { alert('Push from Re-roof spec failed: ' + (e?.response?.data?.detail || e.message)); }
    setRebuilding(null);
  }, [api, appraisalId, clearSelection, load]);

  // Open the confirm modal — fetches a preview so we can show "this
  // will delete X lines from Diagram + Y from Option A". User can
  // change between replace_diagram / replace_all inside the modal.
  const openPushConfirm = useCallback(async ({ kind, label, specId }) => {
    setRebuildMenuOpen(false);
    const source = kind === 'diagram' ? 'roof_diagram' : 'reroof_builder';
    const defaultMode = kind === 'diagram' ? 'replace_diagram' : 'replace_same';
    setPushConfirm({ kind, label, specId, mode: defaultMode, preview: null, loading: true });
    try {
      const params = new URLSearchParams({ source, mode: defaultMode });
      if (kind === 'spec') params.set('spec_id', specId);
      const { data } = await api.get(`/appraisals/${appraisalId}/push-impact?${params.toString()}`);
      setPushConfirm((prev) => prev ? { ...prev, preview: data, loading: false } : null);
    } catch (e) {
      setPushConfirm((prev) => prev ? { ...prev, preview: { would_delete: 0, breakdown: {} }, loading: false } : null);
    }
  }, [api, appraisalId]);

  // Re-fetch preview when the operator flips between replace_diagram /
  // replace_all / replace_same inside the open modal.
  useEffect(() => {
    if (!pushConfirm || !pushConfirm.mode || pushConfirm.mode === 'merge') return;
    let cancelled = false;
    (async () => {
      const source = pushConfirm.kind === 'diagram' ? 'roof_diagram' : 'reroof_builder';
      const params = new URLSearchParams({ source, mode: pushConfirm.mode });
      if (pushConfirm.kind === 'spec') params.set('spec_id', pushConfirm.specId);
      try {
        const { data } = await api.get(`/appraisals/${appraisalId}/push-impact?${params.toString()}`);
        if (!cancelled) setPushConfirm((prev) => prev ? { ...prev, preview: data } : null);
      } catch (e) { /* swallow */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushConfirm?.mode]);

  // iter313c cleanup — deleted `handlePushFromDiagram` and
  // `handlePushFromSpec` (orphan handlers superseded by
  // `openPushConfirm` + `runPushDiagram('merge')` / `runPushSpec`).
  // Zero JSX callers confirmed by grep.

  // Lazy-load the spec list the first time the rebuild menu is opened
  useEffect(() => {
    if (rebuildMenuOpen && reroofSpecs.length === 0) rebuildLoadSpecs();
  }, [rebuildMenuOpen, reroofSpecs.length, rebuildLoadSpecs]);

  // Grand totals (live). Iter150 — Spouting lines respect mitre extras
  // from the gutter context so customer/cost reflects the new length.
  const grand = useMemo(() => {
    const mitreExtraM = (Number(gutterCtx?.mitreExtraMm) || 0) / 1000;
    const out = { customer: 0, cost: 0, mat_profit: 0, lab_profit: 0, lines: 0 };
    WORK_TYPES.forEach(wt => (groups[wt.key] || []).forEach(l => {
      const t = lineTotals(l, wt.key === 'Spouting' ? mitreExtraM : 0);
      out.customer += t.customer_charge;
      out.cost += t.cost_to_us;
      out.mat_profit += t.materials_profit;
      out.lab_profit += t.labour_profit;
      if (l.type_name && Number(l.quantity) > 0) out.lines += 1;
    }));
    otherLines.forEach(l => {
      const t = lineTotals(l, 0);
      out.customer += t.customer_charge;
      out.cost += t.cost_to_us;
      out.mat_profit += t.materials_profit;
      out.lab_profit += t.labour_profit;
      if (l.type_name && Number(l.quantity) > 0) out.lines += 1;
    });
    out.profit = out.mat_profit + out.lab_profit;
    out.margin_pct = out.customer > 0 ? (out.profit / out.customer) * 100 : 0;
    return out;
  }, [groups, gutterCtx, otherLines]);

  // Auto-default the material pickers from EITHER the persisted Primary
  // material (set by the operator on a previous visit) or, as a
  // fallback, the property's roof_type. Cascades to all
  // MATERIAL_SECTIONS so a Galv roof automatically narrows Sheet /
  // Ridge / Valley / Barge / Flashing / Fastening. Operator can still
  // override.
  // Iter192 — also flips existing component rows whose material is
  // EMPTY (legacy data) onto the section target so the operator
  // doesn't have to re-cascade by hand every visit.
  useEffect(() => {
    if (!roofType && !primaryMaterial) return;
    // Iter192 — wait until ANY of the MATERIAL_SECTIONS rate cards has
    // loaded. Previously this gated on rates.Flashing.length, which
    // mis-fires when the appraisal has no Flashing lines (rates are
    // lazy-loaded per work type) → primary material cascade silently
    // skipped on load. A4999 was the smoking gun.
    const anyRatesLoaded = MATERIAL_SECTIONS.some(s => (rates[s] || []).length > 0);
    if (!anyRatesLoaded) return;
    // Skip if the operator has already touched ANY section's selection
    // in this session.
    const touched = MATERIAL_SECTIONS.some(s => materialBySection[s] && materialBySection[s] !== 'all');
    if (touched) return;
    // Persisted Primary material wins over the roof-type guess.
    let suggested = canonicalMaterial(primaryMaterial) || null;
    // Jun 2026 — only guess from the roof type when it has exactly one primary material (Concrete / Clay /
    // Polycarbonate). Iron & co. have several (Galv/Zinc/Maxam/Alu) → no guess, so Zinc isn't forced onto Galv rows.
    if (!suggested && roofType) {
      const opts = primaryMaterialsForRoof(roofType);
      if (opts.length === 1) suggested = opts[0];
    }
    if (!suggested) return;
    const next = {};
    const sectionTargets = {};
    MATERIAL_SECTIONS.forEach(sect => {
      // Iter192 — apply per-section override so Fastening (finish-based)
      // gets "Coloured" instead of the raw "Maxx" steel-grade.
      const target = (PRIMARY_TO_SECTION_OVERRIDE[sect] || {})[suggested] || suggested;
      const has = (rates[sect] || []).some(r => rateHasMaterial(sect, r, target));
      if (has) {
        next[sect] = target;
        sectionTargets[sect] = target;
      }
    });
    if (Object.keys(next).length > 0) {
      setMaterialBySection(prev => ({ ...prev, ...next }));
      // Iter192 — flip rows whose material is currently blank (legacy
      // data or operator-skipped) onto the section target. Rows that
      // already have a material are left alone so the operator's
      // manual overrides survive.
      setGroups(prev => {
        const updated = { ...prev };
        let dirtyTouched = false;
        MATERIAL_SECTIONS.forEach(sect => {
          const target = sectionTargets[sect];
          if (!target) return;
          const arr = updated[sect] || [];
          const empties = arr.filter(l => !l.material && !isBuilderLine(l)).length;
          if (empties > 0) {
            dirtyTouched = true;
            updated[sect] = arr.map(l => (l.material || isBuilderLine(l)) ? l : { ...l, material: target });
          }
        });
        if (dirtyTouched) setDirty(true);
        return updated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roofType, primaryMaterial, rates.Flashing, rates.Sheet, rates.Ridge, rates.Valley, rates.Barge, rates.Fastening]);

  // ─── Quotation Type cascade ─────────────────────────────────────────────
  // (state lives above the early `loading` return so hooks run in a
  // consistent order; the helper functions that operate on this state
  // are plain functions defined further down since they reference
  // post-load values like materialListBySection and cascadeMaterial.)
  const [buildConfirmed, setBuildConfirmed] = useState(false);
  const loadBuildConfirmed = useCallback(async () => {
    try { const { data } = await api.get(`/appraisals/${appraisalId}/build/confirmation`); setBuildConfirmed(!!data?.build_confirmation?.confirmed); }
    catch { setBuildConfirmed(false); }
  }, [api, appraisalId]);
  useEffect(() => { loadBuildConfirmed(); }, [loadBuildConfirmed, refreshKey]);
  useEffect(() => {
    const h = async () => {
      try { const { data } = await api.get(`/appraisals/${appraisalId}`); setQuoteType(data?.quote_type_ref || ''); } catch { /* ignore */ }
    };
    window.addEventListener('rtr:quote-type-changed', h);
    return () => window.removeEventListener('rtr:quote-type-changed', h);
  }, [api, appraisalId]);

  // v10.29 Sprint 4a · Slice C (2026-02c) — Fast-jump target handler.
  // Fires when `?focus_section=<wt>` appears in the URL (Reroof Builder
  // fast-jump). Opens the matching accordion, scrolls it into view,
  // flashes a 2s highlight, then clears the param.
  // N2 (2026-02c) — graceful 404 fallback: if the section element is
  // missing (bad wt name, or component_type not on this appraisal),
  // toast an info + do nothing else. Never crash.
  // Placed BEFORE the `if (loading)` early-return so hook order stays
  // stable across renders (React rules-of-hooks).
  useEffect(() => {
    const target = searchParams.get('focus_section');
    if (!target) return;
    if (loading) return; // wait for the sections to render
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="comp-section-${target}"]`);
      if (!el) {
        try { toast.info(`Components tab doesn't have a "${target}" section on this appraisal.`); }
        catch { /* toast might be lazy-loaded */ }
      } else {
        setOpenTypes(prev => {
          if (prev.has(target)) return prev;
          const next = new Set(prev); next.add(target); return next;
        });
        requestAnimationFrame(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          el.classList.add('reroof-jump-flash');
          setTimeout(() => el.classList.remove('reroof-jump-flash'), 2000);
        });
      }
      const next = new URLSearchParams(searchParams);
      next.delete('focus_section');
      setSearchParams(next, { replace: true });
    }, 120);
    return () => clearTimeout(t);
  }, [searchParams, loading, setSearchParams]);

  // Embedded mode (e.g. Repairs builder) — open the requested sections straight away.
  const onlyKey = onlySections ? onlySections.join('|') : '';
  useEffect(() => {
    if (!onlyKey || loading) return;
    onlyKey.split('|').forEach(k => ensureRates(k));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyKey, loading]);

  if (loading) return <div className="text-center py-12 text-slate-400">Loading components & rate cards…</div>;

  // ─── Per-section material narrow-filter ─────────────────────────────────
  // The rate cards on Sheet, Ridge, Valley, Barge, and Flashing are long
  // enough (hundreds → 1000+ rows) that bucketing by material gives the
  // operator a useful drill-down. Default selection auto-derives from the
  // property's roof_type, but the inspector can override per section.
  const materialListBySection = {};
  MATERIAL_SECTIONS.forEach(sect => {
    materialListBySection[sect] = buildMaterialBuckets(sect, rates[sect] || []);
  });
  // Legacy aliases kept so the rest of the function reads cleanly.
  const flashingRates = rates.Flashing || [];
  const flashingMaterialList = materialListBySection.Flashing || [];

  // ─── Primary-material cascade ───────────────────────────────────────────
  // The "Primary material" master picker at the top of the Components tab
  // cascades the chosen material to ALL 5 material sections in one click
  // AND updates every existing component line's per-row material chip
  // (so e.g. R1 / V1 / Sh1 all flip to the new material without the
  // operator having to touch each row). Persists the headline choice on
  // the appraisal so it survives reload. Sections whose rate card
  // doesn't stock the chosen material are left on their current
  // selection — the Primary badge still highlights the user's intent.
  const cascadeMaterial = (mat) => {
    const next = {};
    // Iter188+ — collect a per-section summary for the toast.
    const sectionSummary = [];
    MATERIAL_SECTIONS.forEach(sect => {
      const available = (materialListBySection[sect] || []).map(b => b.key);
      // Iter188 — apply per-section override (e.g. Fastening: Maxx → Coloured)
      // so finish-based sections cascade to their equivalent bucket.
      const sectionTarget = (PRIMARY_TO_SECTION_OVERRIDE[sect] || {})[mat] || mat;
      if (mat === 'all') next[sect] = 'all';
      else if (available.includes(sectionTarget)) {
        next[sect] = sectionTarget;
        sectionSummary.push({ sect, target: sectionTarget });
      } else next[sect] = (materialBySection[sect] || 'all');
    });
    setMaterialBySection(next);

    // Record + persist the operator's headline intent so it survives
    // reload and so the master picker keeps highlighting the chosen
    // material even when individual section filters diverge later.
    const newPrimary = mat === 'all' ? null : mat;
    setPrimaryMaterial(newPrimary);
    api.patch(`/appraisals/${appraisalId}/primary-material`, { primary_material: newPrimary })
       .catch(e => console.error('Failed to persist primary material', e));

    // Cascade to existing component lines: every row in a section that
    // has at least one rate-row matching the target material (raw OR the
    // section-specific override) gets its `material` chip flipped. Rows
    // in sections with no matching rate row are left untouched.
    if (mat !== 'all') {
      let touched = false;
      let touchedRows = 0;
      setGroups(prev => {
        const updated = { ...prev };
        MATERIAL_SECTIONS.forEach(sect => {
          // Iter188 — apply per-section override (Fastening etc.) and
          // probe the SECTION's rate card via detectMaterialFor instead
          // of relying on the pill-bucket list. This catches single-row
          // materials that buildMaterialBuckets used to filter out.
          const sectionTarget = (PRIMARY_TO_SECTION_OVERRIDE[sect] || {})[mat] || mat;
          const sectionRates  = rates[sect] || [];
          const sectionHas = sectionRates.some(r => rateHasMaterial(sect, r, sectionTarget));
          if (!sectionHas) return;
          const arr = updated[sect] || [];
          const toFlip = arr.filter(l => l.material !== sectionTarget && !isBuilderLine(l)).length;
          if (toFlip > 0) {
            touched = true;
            touchedRows += toFlip;
            updated[sect] = arr.map(l => (l.material === sectionTarget || isBuilderLine(l)) ? l : { ...l, material: sectionTarget });
          }
        });
        return updated;
      });
      if (touched) setDirty(true);

      // Iter188 — Spouting/Gutter cascade. The spouting builder uses a
      // separate `gutter_builder.material_key` config block with its
      // own vocabulary. Map the primary material to the equivalent
      // gutter material so the Spouting tab also flips.
      const gutterMat = PRIMARY_TO_GUTTER_MATERIAL[mat];
      if (gutterMat) {
        (async () => {
          try {
            const { data: g } = await api.get(`/guttering/appraisals/${appraisalId}/gutter-builder/config`);
            const current = g?.config || g || {};
            if (current.material_key !== gutterMat) {
              await api.put(
                `/guttering/appraisals/${appraisalId}/gutter-builder/config`,
                { ...current, material_key: gutterMat },
              );
            }
          } catch (_e) { /* silent — gutter config may not exist yet */ }
        })();
      }

      // Iter188+ — show a single summary toast so the operator can
      // see at a glance what the cascade did (which sections + how
      // many lines flipped + whether Spouting was bumped).
      const bySection = {};
      sectionSummary.forEach(({ sect, target }) => {
        const arr = (bySection[target] = bySection[target] || []);
        arr.push(sect);
      });
      const summaryLines = Object.entries(bySection).map(([target, sects]) =>
        `${sects.join(', ')} → ${target}`,
      );
      if (gutterMat) summaryLines.push(`Spouting → ${gutterMat}`);
      const head = sectionSummary.length
        ? `Cascaded to ${sectionSummary.length} section${sectionSummary.length === 1 ? '' : 's'} · ${touchedRows} row${touchedRows === 1 ? '' : 's'} updated`
        : `No section stocks ${mat} — Primary Material recorded.`;
      toast.success(head, { description: summaryLines.join(' · ') || undefined, duration: 4500 });
    }
  };
  // Union of materials available across the 5 sections — drives the
  // master picker's option list.
  const masterMaterials = (() => {
    const seen = new Map();
    MATERIAL_SECTIONS.forEach(sect => {
      (materialListBySection[sect] || []).forEach(({ key, count }) => {
        seen.set(key, (seen.get(key) || 0) + count);
      });
    });
    // Jun 2026 — headline strip only offers the roof type's primary materials.
    const allowed = primaryMaterialsForRoof(roofType);
    return [...seen.entries()]
      .filter(([key]) => allowed.includes(key))
      .sort((a, b) => b[1] - a[1])
      .map(([key, count]) => ({ key, count }));
  })();
  // Decide what to show in the master strip: the persisted primaryMaterial
  // beats everything (operator declared intent — sections may diverge but
  // the badge keeps the headline). Otherwise if all 5 sections have the
  // same selection that's the value, else "mixed".
  const allSelections = MATERIAL_SECTIONS.map(s => materialBySection[s] || 'all');
  const allSame = allSelections.every(v => v === allSelections[0]);
  const masterValue = primaryMaterial
    ? canonicalMaterial(primaryMaterial)
    : (allSame ? allSelections[0] : 'mixed');

  // C4f (2026-02c) — bag every prop the SectionAccordion needs so we
  // don't have to drill 60 individual props into every section. State
  // still lives in the parent · this is a pointer bag, not a state
  // container.
  const sectionCtx = {
    groups, rates, openTypes,
    sheetSectionProfile, setSheetSectionProfile, sheetProfileNames, sheetProfileMap,
    roofProfile, roofType, roofBrand, roofSubtype, roofM2, primaryMaterial, tileProfileOverride,
    materialListBySection, materialBySection, setMaterialBySection,
    sectionShowAll, setSectionShowAll,
    gutterCtx, setGutterCtx,
    flashingTemplates, valleyProfileOptions, bargeProfileOptions,
    quoteType, appraisalId, facePinLengths, profileLists,
    selectedKeys,
    setGroups, setDirty, setRates,
    toggleOpen, addLine, removeLine, duplicateLastLine, updateLine, onPickType,
    cascadeFieldToSiblings, toggleSelected, selectAllInSection,
    onLinkDektite, onUnlinkDektite, onChangeDektite,
    onLinkBacktray, onUnlinkBacktray, onChangeBacktray,
    pushClearSiliconeToBom, upsertExtraClipsLine, upsertMultidekAuxLines,
    upsertFoamFillerLines, upsertEdgingLines, upsertBedPointAuxLines,
    pushBracketsToBom,
    scrollToFastMenu, jumpToDiagram,
    api, isAdmin,
  };

  if (onlySections) return (
    <RateDetailProvider api={api}>
      <div data-testid="components-embedded" className="space-y-2"
        onChangeCapture={() => setEmbedTouched(true)} onClickCapture={() => setEmbedTouched(true)}>
        {WORK_TYPES.filter(wt => onlySections.includes(wt.key)).map(wt => (
          <SectionAccordion key={wt.key} wt={wt} ctx={{ ...sectionCtx, openTypes: new Set([...openTypes, ...onlySections]) }} />
        ))}
        <div className="flex items-center justify-end gap-2">
          {savedAt && !dirty && <span data-testid="components-embedded-saved" className="text-xs text-emerald-700 font-semibold">Saved {savedAt.toLocaleTimeString()}</span>}
          {dirty && autosaveCountdown > 0 && <span className="text-xs text-sky-700 font-semibold">Autosaving in {(autosaveCountdown / 1000).toFixed(0)}s…</span>}
          {dirty && embedTouched && !saving && autosaveCountdown <= 0 && <span className="text-xs text-amber-700 font-semibold">Unsaved changes</span>}
          <button data-testid="components-embedded-save" onClick={() => saveRef.current?.()} disabled={!dirty || !embedTouched || saving || hasBlockingErrors}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-xs font-semibold disabled:opacity-50 transition-colors">
            <Save size={12} /> {saving ? 'Saving…' : 'Save flashings'}
          </button>
        </div>
      </div>
    </RateDetailProvider>
  );

  return (
    <RateDetailProvider api={api}>
    <>
    {/* C3c-α · Validation errors from the rules engine (VAL-* rules).
        Sits at the very top of the tab · fires when engine returns any
        validation_errors[] · currently: reroof-without-profile. Blocking
        errors disable the Save button (see save-components-btn below). */}
    <ValidationErrorBanner errors={validationErrors} />
    {/* Go-Live Batch S · Manual build lock strip — top of Components tab.
        Lets the operator confirm the build is final. Confirmed builds
        refuse re-pushes/diagram edits (row-level edits stay allowed under
        audit). Read-only when unconfirmed. */}
    <div data-testid="components-qt-banner" className="rounded-md border border-violet-200 bg-violet-50/40 px-3 py-2 mb-2 flex items-center gap-3 flex-wrap">
      {isChildAppraisal && childAppraisalMeta?.builder_origin ? (
        <span className="text-xs text-slate-700" data-testid="components-qt-readonly">
          <b>Quotation Type:</b> {childAppraisalMeta.quotation_type || <em className="text-amber-700">not picked yet</em>} <span className="text-slate-500">(set on the builder banner)</span>
        </span>
      ) : (
        <QuotationTypeSelect api={api} quoteId={appraisalId} roofType={roofType} current={quoteType || childAppraisalMeta?.quotation_type}
          disabled={buildConfirmed} testId="components-qt" onSaved={(ref) => setQuoteType(ref)} />
      )}
      {buildConfirmed && !isChildAppraisal && <span className="text-[11px] text-slate-500">Locked — click Revise to change.</span>}
    </div>
    <BuildConfirmationStrip
      appraisalId={appraisalId}
      variant="components"
      refreshKey={refreshKey}
      onChange={loadBuildConfirmed}
    />
    {/* C4h (2026-02c) · Engine Status Badge — right-aligned chip that
        tells the operator whether the rules engine is driving the
        form. Reads telemetry from useCascadeEngine hook. */}
    <div className="flex items-center justify-end gap-2 mb-2">
      <SaveDocButton
        appraisalId={appraisalId}
        docKind="quotation_detailed"
        label="Save Quote PDF"
        variant="outline"
        size="sm"
        expandChildren={false}
        testId="components-save-quote-pdf"
      />
      <EngineStatusBadge
        loading={engineLoading}
        rulesFired={rulesFired}
        rulesEvaluated={rulesEvaluated}
        engineVersionHash={engineVersionHash}
        hasBlockingErrors={hasBlockingErrors}
      />
    </div>
    {/* iter303ak-fix24 · Child-appraisal Pull-from-Builder + Clear banner.
        Only renders when this Components tab is opened on a CHILD
        appraisal (parent_appraisal_id set). Gives the operator a
        one-click "sync from the underlying builder" + "wipe auto-
        imported rows" that mirrors the auto-push logic. */}
    {isChildAppraisal && childAppraisalMeta && (
      <div data-testid="child-builder-sync-banner" className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 mb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-emerald-900">
            <div className="font-semibold flex items-center gap-1.5">
              <RefreshCw size={12} />
              Child appraisal · builder sync
              {childAppraisalMeta.appraisal_no && (
                <span className="ml-1 text-[10px] font-bold uppercase tracking-widest text-emerald-800 bg-white border border-emerald-300 rounded px-1.5 py-0.5">
                  A{childAppraisalMeta.appraisal_no}
                </span>
              )}
              {childAppraisalMeta.quote_type_ref && (
                <span className="ml-1 text-[10px] font-semibold text-emerald-700 bg-white border border-emerald-200 rounded px-1.5 py-0.5">
                  {childAppraisalMeta.quote_type_ref}{childAppraisalMeta.variant_label ? ` · ${childAppraisalMeta.variant_label}` : ''}
                </span>
              )}
            </div>
            <div className="text-emerald-800 mt-1">
              {childAppraisalMeta.auto_pushed_at ? (
                <>
                  Last builder pull: <b>{new Date(childAppraisalMeta.auto_pushed_at).toLocaleString()}</b>
                  {childAppraisalMeta.auto_push_result?.inserted > 0 && <> · <b>{childAppraisalMeta.auto_push_result.inserted}</b> line{childAppraisalMeta.auto_push_result.inserted === 1 ? '' : 's'} inserted</>}
                  {childAppraisalMeta.auto_push_result?.skipped && <span className="ml-1 text-amber-800">· Last pull skipped ({childAppraisalMeta.auto_push_result?.error || 'no data'})</span>}
                </>
              ) : (
                <span>No builder pull recorded yet — click <b>Pull from builder</b> to sync this quote's component data.</span>
              )}
            </div>
          </div>
          <div className={`flex items-center gap-2 shrink-0 ${childAppraisalMeta.builder_origin ? 'rtr-builder-quote' : ''}`}>
            <button
              type="button"
              data-testid="child-pull-from-builder-btn"
              onClick={handlePullFromChildBuilder}
              disabled={!!childActionBusy}
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              title="Re-run the builder → components sync for this quote. Uses replace_same so manual rows survive."
            >
              <RefreshCw size={12} className={childActionBusy === 'pull' ? 'animate-spin' : ''} />
              {childActionBusy === 'pull' ? 'Pulling…' : 'Pull from builder (this quote)'}
            </button>
            {!childAppraisalMeta.builder_origin && (
            <button
              type="button"
              data-testid="child-clear-auto-btn"
              onClick={handleClearAutoImported}
              disabled={!!childActionBusy}
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-rose-700 bg-white border border-rose-300 hover:bg-rose-50 disabled:opacity-50 inline-flex items-center gap-1.5"
              title="Wipe every auto-imported row on this quote. Manual rows survive."
            >
              <Trash2 size={12} />
              {childActionBusy === 'clear' ? 'Clearing…' : 'Clear auto-imported'}
            </button>
            )}
          </div>
        </div>
      </div>
    )}
    {/* Push-confirmation modal — surfaces a count of lines about to be
        deleted before any destructive push is fired. Three modes:
        replace_same (default, idempotent), replace_diagram (wipe only
        the previous diagram push), replace_all (wipe every auto-
        imported row across both diagram + every spec). */}
    {pushConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="push-confirm-modal">
        <div className="bg-white rounded-md shadow-xl ring-1 ring-slate-200 w-full max-w-lg p-5">
          <div className="flex items-start gap-3 mb-3">
            <div className="rounded-full bg-orange-100 p-2 text-orange-700"><RefreshCw size={18} /></div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-[#0F172A]">
                Replace components with <span className="text-[#D35400]">{pushConfirm.label}</span>?
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Pick what to wipe before the new push runs. Manual rows (rows you typed by hand) are never deleted.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPushConfirm(null)}
              className="text-slate-400 hover:text-slate-700"
              data-testid="push-confirm-close"
            ><X size={16} /></button>
          </div>

          <div className="space-y-2 mb-4">
            {pushConfirm.kind === 'spec' && (
              <label className={`flex items-start gap-2 p-2 rounded ring-1 cursor-pointer ${pushConfirm.mode === 'replace_same' ? 'ring-emerald-300 bg-emerald-50' : 'ring-slate-200 hover:bg-slate-50'}`}>
                <input
                  type="radio"
                  name="push-mode"
                  checked={pushConfirm.mode === 'replace_same'}
                  onChange={() => setPushConfirm((p) => ({ ...p, mode: 'replace_same' }))}
                  data-testid="push-confirm-mode-same"
                  className="mt-0.5"
                />
                <div className="text-xs">
                  <div className="font-semibold text-[#0F172A]">Replace this option only <span className="text-slate-400 font-normal">(idempotent)</span></div>
                  <div className="text-slate-500">Wipe only the lines previously pushed from <em>{pushConfirm.label}</em>. Diagram + other specs survive.</div>
                </div>
              </label>
            )}
            <label className={`flex items-start gap-2 p-2 rounded ring-1 cursor-pointer ${pushConfirm.mode === 'replace_diagram' ? 'ring-orange-300 bg-orange-50' : 'ring-slate-200 hover:bg-slate-50'}`}>
              <input
                type="radio"
                name="push-mode"
                checked={pushConfirm.mode === 'replace_diagram'}
                onChange={() => setPushConfirm((p) => ({ ...p, mode: 'replace_diagram' }))}
                data-testid="push-confirm-mode-diagram"
                className="mt-0.5"
              />
              <div className="text-xs">
                <div className="font-semibold text-[#0F172A]">Replace diagram rows only</div>
                <div className="text-slate-500">Wipe everything previously pushed from the Roof Diagram. Spec pushes survive.</div>
              </div>
            </label>
            <label className={`flex items-start gap-2 p-2 rounded ring-1 cursor-pointer ${pushConfirm.mode === 'replace_all' ? 'ring-rose-300 bg-rose-50' : 'ring-slate-200 hover:bg-slate-50'}`}>
              <input
                type="radio"
                name="push-mode"
                checked={pushConfirm.mode === 'replace_all'}
                onChange={() => setPushConfirm((p) => ({ ...p, mode: 'replace_all' }))}
                data-testid="push-confirm-mode-all"
                className="mt-0.5"
              />
              <div className="text-xs">
                <div className="font-semibold text-[#0F172A]">Replace everything auto-imported</div>
                <div className="text-slate-500">Wipe every row that came from the diagram OR any spec push. Manual rows untouched.</div>
              </div>
            </label>
          </div>

          {/* Live preview — counts pulled from /push-impact. Re-fetches
              whenever the operator flips between modes. */}
          <div className="rounded ring-1 ring-slate-200 bg-slate-50 px-3 py-2 mb-4" data-testid="push-confirm-preview">
            {!pushConfirm.preview ? (
              <div className="text-xs text-slate-400 italic">Calculating impact…</div>
            ) : pushConfirm.preview.would_delete === 0 ? (
              <div className="text-xs text-emerald-700"><strong>0 lines</strong> will be deleted — clean slate.</div>
            ) : (
              <div className="text-xs">
                <div className="font-semibold text-rose-700">
                  {pushConfirm.preview.would_delete} line{pushConfirm.preview.would_delete === 1 ? '' : 's'} will be deleted:
                </div>
                <ul className="mt-1 ml-3 list-disc text-slate-600 space-y-0.5">
                  {Object.entries(pushConfirm.preview.breakdown || {}).map(([k, v]) => (
                    <li key={k}><span className="font-mono-num">{v}</span> from <em>{k.startsWith('Spec ') ? k : (k === 'roof_diagram' ? 'Roof Diagram' : k === 'reroof_builder' ? 'Re-roof spec' : k)}</em></li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={() => setPushConfirm(null)}
              data-testid="push-confirm-cancel"
              className="px-3 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900"
            >Cancel</button>
            <button
              type="button"
              onClick={() => {
                if (pushConfirm.kind === 'diagram') runPushDiagram(pushConfirm.mode);
                else runPushSpec(pushConfirm.specId, pushConfirm.mode);
              }}
              data-testid="push-confirm-go"
              disabled={rebuilding !== null}
              className="px-4 py-2 text-xs font-semibold rounded-md bg-[#D35400] hover:bg-[#A04000] text-white disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <RefreshCw size={12} /> Replace &amp; push
            </button>
          </div>
        </div>
      </div>
    )}

    <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5">
      {/* MAIN — section cards */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <button data-testid="components-expand-all" onClick={openAll} className="text-xs font-semibold text-[#D35400] hover:text-[#A04000] transition-colors">{allOpen ? 'Collapse all' : 'Expand all'}</button>
          <div className="flex items-center gap-3 flex-wrap">
            <UndoToolbar
              appraisalId={appraisalId}
              scope="components"
              refreshKey={undoRefresh}
              onRestored={() => { load(); setUndoRefresh(k => k + 1); }}
              testIdPrefix="components-undo"
            />
            {/* Iter229 — Materials Order List button. Opens a modal with
                the supplier-ready aggregation (Ridge/Valley/Barge/Flashing
                pieces with the 9 m mill + 200 mm lap rule applied). */}
            <button
              type="button"
              data-testid="components-materials-order-btn"
              onClick={() => setMaterialsOrderOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-emerald-50 text-emerald-800 rounded-md text-xs font-semibold ring-1 ring-emerald-300 transition-colors"
              title="Aggregate all Ridge / Valley / Barge / Flashing lines into a supplier-ready order list"
            >
              📋 Materials order list
            </button>
            {/* Clear & Rebuild dropdown — wipes the slate and (optionally)
                re-imports from the roof diagram or a Re-roof Builder spec.
                The Undo toolbar lets the operator roll back if needed. */}
            {!(isChildAppraisal && childAppraisalMeta?.builder_origin) && (
            <div className="relative">
              <button
                type="button"
                data-testid="components-clear-rebuild-btn"
                onClick={() => setRebuildMenuOpen((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-orange-50 text-orange-800 rounded-md text-xs font-semibold ring-1 ring-orange-300 transition-colors"
                title="Clear all components or re-import from the diagram / a Re-roof spec"
              >
                <RefreshCw size={12} strokeWidth={2.5} /> Clear &amp; rebuild
                <ChevronDown size={12} />
              </button>
              {rebuildMenuOpen && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setRebuildMenuOpen(false)} />
                  <div
                    className="absolute right-0 mt-1 z-30 w-96 bg-white rounded-md ring-1 ring-slate-200 shadow-lg p-1 text-sm"
                    data-testid="components-rebuild-menu"
                  >
                    <button
                      type="button"
                      onClick={handleClearAll}
                      disabled={rebuilding !== null}
                      data-testid="components-rebuild-clear-all"
                      className="w-full text-left px-3 py-2 rounded hover:bg-rose-50 text-rose-800 disabled:opacity-50 flex items-center gap-2"
                    >
                      <Trash2 size={13} /> Clear all components
                    </button>
                    <div className="border-t border-slate-100 my-1" />
                    <div className="px-3 py-1 text-[10px] uppercase tracking-wide font-bold text-slate-500">Push from Roof Diagram</div>
                    <div className="px-2 py-1 flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => openPushConfirm({ kind: 'diagram', label: 'Roof Diagram' })}
                        disabled={rebuilding !== null}
                        data-testid="components-rebuild-from-diagram"
                        className="flex-1 text-left px-3 py-2 rounded hover:bg-orange-50 text-orange-800 disabled:opacity-50 flex items-center gap-2 text-xs font-semibold ring-1 ring-orange-200"
                        title="Replace existing rows with a fresh diagram push (you'll see a confirm step)"
                      >
                        <RefreshCw size={13} /> Replace
                      </button>
                      <button
                        type="button"
                        onClick={() => runPushDiagram('merge')}
                        disabled={rebuilding !== null}
                        data-testid="components-rebuild-from-diagram-merge"
                        className="flex-1 text-left px-3 py-2 rounded hover:bg-slate-100 text-slate-700 disabled:opacity-50 flex items-center gap-2 text-xs font-semibold ring-1 ring-slate-200"
                        title="Append diagram-sourced rows on top of whatever is already there (no wipe)"
                      >
                        <Plus size={13} /> Add (keep)
                      </button>
                    </div>
                    <div data-testid="components-rebuild-spec-section">
                    <div className="border-t border-slate-100 my-1" />
                    <div className="px-3 py-1 text-[10px] uppercase tracking-wide font-bold text-slate-500">Push from Re-roof spec</div>
                    {reroofSpecs.length === 0 && (
                      <div className="px-3 py-2 text-xs text-slate-400 italic" data-testid="components-rebuild-no-specs">No re-roof specs on this appraisal yet.</div>
                    )}
                    {reroofSpecs.map((s) => (
                      <div key={s._id} className="px-2 py-1">
                        <div className="text-[11px] font-semibold text-slate-700 truncate mb-1 px-1">
                          {s.is_chosen && <span className="text-amber-700 mr-1">★</span>}
                          {s.name || 'Option'} — {s.material_kind || '—'}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => openPushConfirm({ kind: 'spec', label: s.name || 'Option', specId: s._id })}
                            disabled={rebuilding !== null}
                            data-testid={`components-rebuild-from-spec-${s._id}`}
                            className="flex-1 text-left px-3 py-2 rounded hover:bg-emerald-50 text-emerald-800 disabled:opacity-50 flex items-center gap-2 text-xs font-semibold ring-1 ring-emerald-200"
                            title="Replace existing rows with this spec's lines (you'll see a confirm step)"
                          >
                            <RefreshCw size={13} /> Replace
                          </button>
                          <button
                            type="button"
                            onClick={() => runPushSpec(s._id, 'merge')}
                            disabled={rebuilding !== null}
                            data-testid={`components-rebuild-from-spec-${s._id}-merge`}
                            className="flex-1 text-left px-3 py-2 rounded hover:bg-slate-100 text-slate-700 disabled:opacity-50 flex items-center gap-2 text-xs font-semibold ring-1 ring-slate-200"
                            title="Append this spec's rows on top of whatever is already there (no wipe)"
                          >
                            <Plus size={13} /> Add (keep)
                          </button>
                        </div>
                      </div>
                    ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            )}
            {savedAt && !dirty && <span className="text-xs text-emerald-700 font-semibold">Saved {savedAt.toLocaleTimeString()}</span>}
            {dirty && !saving && autosaveCountdown <= 0 && (
              <span className="text-xs text-amber-700 font-semibold">Unsaved changes</span>
            )}
            {/* Iter190 — autosave countdown badge */}
            {dirty && autosaveCountdown > 0 && (
              <span data-testid="autosave-countdown" className="text-xs text-sky-700 font-semibold">
                Autosaving in {(autosaveCountdown / 1000).toFixed(autosaveCountdown < 1000 ? 1 : 0)}s…
              </span>
            )}
            {saving && (
              <span className="text-xs text-slate-500 italic font-semibold flex items-center gap-1">
                <RefreshCw size={11} className="animate-spin" /> Saving…
              </span>
            )}
            {/* Iter190 — autosave toggle. Persisted in localStorage. */}
            <button
              data-testid="autosave-toggle"
              onClick={() => setAutosaveEnabled(v => !v)}
              className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded border transition ${autosaveEnabled ? 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-slate-300 text-slate-500 hover:bg-slate-50'}`}
              title={autosaveEnabled
                ? 'Autosave is ON — edits save 3 seconds after you stop typing. Click to switch to manual save.'
                : 'Autosave is OFF — click "Save Components" to commit edits. Click here to switch to autosave.'}
            >
              Autosave {autosaveEnabled ? 'on' : 'off'}
            </button>
            <button
              data-testid="save-components-btn"
              onClick={() => saveRef.current?.()}
              disabled={!dirty || saving || hasBlockingErrors}
              title={hasBlockingErrors
                ? 'Save blocked · fix the validation errors above first'
                : (saving ? 'Saving…' : 'Save Components')}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold disabled:opacity-50 shadow-sm hover:shadow-md hover:shadow-orange-500/20 transition-all"
            >
              <Save size={14} strokeWidth={2.5} /> {saving ? 'Saving…' : (hasBlockingErrors ? 'Save blocked' : 'Save Components')}
            </button>
          </div>
        </div>

        {/* Bulk-selection toolbar — sticky strip with "Deselect" and
            "Delete N selected" actions. Hidden when nothing is selected. */}
        {selectedKeys.size > 0 && (
          <div
            className="flex items-center gap-3 px-3 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 sticky top-0 z-10"
            data-testid="components-bulk-toolbar"
          >
            <span className="text-xs font-semibold text-rose-800" data-testid="components-bulk-count">
              {selectedKeys.size} line{selectedKeys.size === 1 ? '' : 's'} selected
            </span>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="components-bulk-clear"
            >
              <X size={12} /> Clear
            </button>
            <button
              type="button"
              onClick={deleteSelected}
              data-testid="components-bulk-delete"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded text-xs font-semibold"
            >
              <Trash2 size={12} /> Delete {selectedKeys.size} selected
            </button>
          </div>
        )}

        {/* Primary-material master picker — one-tap cascade that sets the
            material across Sheet / Ridge / Valley / Barge / Flashing so the
            operator can declare "this is a Galv job" / "Maxx job" / etc.
            up-front. Per-section + per-line overrides still apply below. */}
        {masterMaterials.length > 0 && (
          <div data-testid="primary-material-picker" className="rounded-md border border-orange-200 bg-gradient-to-br from-orange-50/60 to-white px-3 py-2 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#D35400]">Primary material</span>
            <button
              type="button"
              data-testid="primary-material-all"
              onClick={() => cascadeMaterial('all')}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${masterValue === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
            >All</button>
            {masterMaterials.map(({ key, count }) => (
              <button
                key={key}
                type="button"
                data-testid={`primary-material-${key.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => cascadeMaterial(key)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${masterValue === key ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
              >
                {key} <span className="opacity-70 ml-0.5">({count})</span>
              </button>
            ))}
            {masterValue === 'mixed' && (
              <span data-testid="primary-material-mixed" className="text-[10px] text-slate-500 italic ml-1">mixed across sections — pick to align all</span>
            )}
            {roofType && (
              <span className="ml-auto text-[10px] text-slate-400 italic">Roof: <span className="font-semibold text-slate-600 not-italic">{roofType}</span></span>
            )}
          </div>
        )}

        {/* Iter257 — Sticky fast-menu chip bar. One chip per visible
            WORK_TYPE, showing the live line count. Clicking expands the
            target section + scrolls it into view. Lives at the top of
            the Components tab so the operator can hop directly from
            (e.g.) Underlay → Sheet without manually scrolling past
            every other section. The "📐 Diagram" chip jumps back to
            the Overview tab where the annotated roof plan lives. */}
        <div
          data-testid="components-fast-menu"
          className="sticky top-0 z-30 bg-white/95 backdrop-blur border border-slate-200 rounded-lg px-3 py-2 shadow-sm"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mr-1">
              Jump to
            </span>
            {WORK_TYPES.filter(wt => wt.key !== 'Face' && wt.key !== 'Length').map(wt => {
              const count = (groups[wt.key] || []).filter(hasRealData).length;
              return (
                <button
                  key={wt.key}
                  type="button"
                  data-testid={`fast-menu-${wt.key}`}
                  onClick={() => jumpToSection(wt.key)}
                  title={`Jump to ${wt.label || wt.key}${count > 0 ? ` (${count} line${count === 1 ? '' : 's'})` : ''}`}
                  className={`group inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full transition-colors ring-1 ${
                    count > 0
                      ? 'bg-white text-slate-700 ring-slate-200 hover:bg-slate-50'
                      : 'bg-slate-50 text-slate-400 ring-slate-100 hover:bg-slate-100'
                  }`}
                >
                  {/* C11-P2.1 (2026-02c) — when the visible label
                      diverges from the internal key (e.g. Aquaseal →
                      "Dektite"), emit an invisible sibling testid so
                      browser-automation can locate the chip by its
                      operator-facing label. Backwards-compat: the
                      primary chip testid keeps the internal-key
                      naming (`fast-menu-Aquaseal`). */}
                  {wt.label && wt.label !== wt.key && (
                    <span
                      data-testid={`fast-menu-${wt.label}`}
                      className="sr-only"
                      aria-hidden="true"
                    />
                  )}
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: wt.colour }}
                  />
                  {wt.label || wt.key}
                  {count > 0 && (
                    <span className="text-[9px] font-mono-num px-1 py-px rounded bg-orange-50 text-[#D35400] ring-1 ring-orange-100">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
            {/* Iter257b — Diagram chip always renders; jumpToDiagram
                now scrolls to the annotator element directly (lives in
                the page header, not Overview tab), so no onJumpTab
                dependency. */}
            <button
              type="button"
              data-testid="fast-menu-diagram"
              onClick={jumpToDiagram}
              title="Jump to the annotated roof diagram at the top of the page."
              className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full bg-sky-50 text-sky-700 ring-1 ring-sky-200 hover:bg-sky-100 transition-colors"
            >
              📐 Diagram
            </button>
          </div>
        </div>

        {WORK_TYPES.filter(wt => {
          // Iter145 — Hide the Face + Length sections from the Components
          // tab. Face pins now alias straight to ReCoat (their area_m²
          // becomes the ReCoat line's length); Length pins are tracked
          // on the diagram canvas but never billed as their own component
          // line. The values still live in the DB if any are present —
          // hidden but not deleted — so a future "Push to Components"
          // will route Face pins through the new ReCoat alias.
          if (wt.key === 'Face' || wt.key === 'Length') return false;
          return true;
        }).map(wt => (
          <SectionAccordion key={wt.key} wt={wt} ctx={sectionCtx} />
        ))}
      </div>

      {/* RIGHT — sticky totals */}
      <aside className="hidden xl:block">
        <div className="sticky top-4 space-y-3">
          <div className="bg-gradient-to-br from-white to-orange-50/30 border border-[#E2E2DF] rounded-md p-5 relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-[3px] bg-[#D35400]" />
            <p className="eyebrow mb-1">Customer Charge</p>
            <p className="text-3xl font-extrabold text-slate-900 font-[Manrope] font-mono-num tracking-tight">{fmt(grand.customer)}</p>
            <p className="text-xs text-slate-500 mt-1 font-mono-num">{grand.lines} active line{grand.lines === 1 ? '' : 's'}</p>
            <p className="text-[10px] text-slate-400 mt-1">Before markup, discounts &amp; GST — see Quote total below.</p>
          </div>
          <QuoteTotalStrip api={api} appraisalId={appraisalId} linesTotal={grand.customer} testid="components-quote-total-strip" />
          {otherLines.length > 0 && (
            <div data-testid="components-other-builder-lines" className="bg-white border border-amber-200 rounded-md p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Builder lines (included above)</p>
              <ul className="mt-1 space-y-0.5">
                {otherLines.map((l, i) => (
                  <li key={l._id || i} className="flex justify-between gap-2 text-[11px] text-slate-600">
                    <span className="truncate">{l.component_type} · {l.type_name}</span>
                    <span className="font-mono-num">{fmt(lineTotals(l, 0).customer_charge)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-3">
            <div className="flex justify-between items-baseline">
              <span className="text-xs text-slate-500">Cost to us</span>
              <span className="text-sm font-bold text-slate-700 font-mono-num">{fmt(grand.cost)}</span>
            </div>
            <div className="border-t border-[#E2E2DF] pt-3 space-y-2">
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-slate-500">Materials profit</span>
                <span className="text-sm font-bold text-emerald-700 font-mono-num">{fmt(grand.mat_profit)}</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-slate-500">Labour profit</span>
                <span className="text-sm font-bold text-emerald-700 font-mono-num">{fmt(grand.lab_profit)}</span>
              </div>
            </div>
            <div className="border-t border-[#E2E2DF] pt-3 flex justify-between items-baseline">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-700">Total profit</span>
              <span className="text-xl font-extrabold text-emerald-700 font-[Manrope] font-mono-num">{fmt(grand.profit)}</span>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-xs text-slate-500 flex items-center gap-1"><TrendingUp size={11} /> Margin</span>
              <span className={`text-sm font-bold font-mono-num ${grand.margin_pct >= 30 ? 'text-emerald-700' : grand.margin_pct >= 15 ? 'text-amber-700' : 'text-rose-600'}`}>{grand.margin_pct.toFixed(1)}%</span>
            </div>
          </div>
          {!isAdmin && (
            <div className="bg-blue-50/60 border border-blue-100 rounded-md p-3 text-xs text-blue-700 flex items-start gap-2">
              <Lock size={13} className="mt-0.5 flex-shrink-0" />
              <span>You can edit components. Changes auto-sync to Quote Items (read-only for non-admins).</span>
            </div>
          )}
        </div>
      </aside>
    </div>
    {/* Iter229 — Materials Order List modal. Aggregates every Ridge /
        Valley / Barge / Flashing line through the 9 m mill + 200 mm lap
        rule into a supplier-ready order summary with copy-to-clipboard. */}
    <MaterialsOrderListModal
      api={api}
      appraisalId={appraisalId}
      open={materialsOrderOpen}
      onClose={() => setMaterialsOrderOpen(false)}
    />
    </>
    </RateDetailProvider>
  );
}

/**
 * SpoutingBracketSummary — Iter149/150.
 *
 * Groups every operator-entered Spouting line by its gutter `type_name`
 * and shows a running-total table so the operator can see the total
 * metres of each gutter type in the appraisal AND the bracket count
 * required to fix them. Iter150 — also rolls up Stop-Ends, Outlets /
 * Droppers, plus the Rivet + Silicone consumable calc.
 *
 * Bracket formula matches GutterBuilderPanel (iter130e/h):
 *   ordered_m   = base_m + mitre_count × mitre_extra_m
 *   bracket_raw = ceil(ordered_m / bracket_span_m)
 *   bracket_qty = ceil(bracket_raw × (1 + buffer_pct/100))
 *
 * Consumables (operator's rule, iter150):
 *   Per dropper (outlet):  4 rivets + 10% silicone tube
 *   Per pair of mitres:    5 rivets + 15% silicone tube   (= 2.5 / 7.5% per mitre)
 *   Per stop end:          4 rivets + 15% silicone tube
 * Rivets summed as floats then ceil'd at grand-total. Silicone summed
 * as fractional tubes then ceil'd. Backward-compat: rows tagged with
 * the legacy [MITRE] notes prefix (iter149 only) are treated as
 * mitre_count=1 when the new field is absent.
 */
// Iter/C4e (2026-02c) — SpoutingBracketSummary extracted to ./AppraisalComponents/SpoutingBracketSummary.jsx

// Iter220 — ComponentLineRow, CapPanel, FlashingTemplatePickerInline and
// SourceBadge were moved to /app/frontend/src/components/shared/ComponentRow/
// so the Repairs builders (Iron / Concrete / Tin-Tile) can adopt the same
// row UX without duplicating ~1500 lines of JSX. The legacy in-file copies
// were removed; this file now uses the shared `ComponentLineRow` imported
// at the top. FlashingFoldsPanel was retired (only used by the dead-code
// `false &&` branch in ComponentLineRow).


// Iter260b — AquasealExtraClipsBar extracted (2026-02c · C4d) →
// ./AppraisalComponents/AquasealExtraClipsBar.jsx


// Iter260e — Multidek Clips & Bracket Screws bar. Sits under the Sheet
// section header. Only renders when ≥1 Multidek sheet line exists. Auto-
// derives clips qty = ceil(Multidek_m² × 6 × (1+buf%)) and screws qty =
// ceil(clips × 3 × (1+buf%)). Buffer + both qtys are operator-editable;
// the bar commits to the appraisal's auto-lines on blur. Reload-safe:
// pre-populates from existing [AUTO:multidek_(clips|screws)] lines.
// Iter/C4e (2026-02c) — MultidekClipsBar extracted to ./AppraisalComponents/MultidekClipsBar.jsx




// Iter260g — Bedding & Pointing materials auto-bar. Renders under
// Ridge and Barge sections (one instance each). Detects bed-and-point
// vs point-only lines by name and derives mortar + pointing qtys.
// Plasticizer volume is display-only (no auto-line, no $ impact).
// Iter/C4e (2026-02c) — BedPointMaterialsBar extracted to ./AppraisalComponents/BedPointMaterialsBar.jsx


// Iter260k — Foam Filler bar for Sheet section. Detects unique sheet
// profiles in use, lets operator pick Narrow/Wide/Both/None per profile,
// plus 3 flat strip meter inputs. Qty per profile = Σ sheet quantities
// (sheet count, not meters). Flat strips = operator-entered meters.
// Iter/C4e (2026-02c) — FoamFillerBar extracted to ./AppraisalComponents/FoamFillerBar.jsx


// Iter260l — EdgingCard + EdgingBar extracted (2026-02c · C4d) →
// ./AppraisalComponents/FlashingEdgingBar.jsx
